import { observer } from "mobx-react-lite";
import SmartAppStep from "../../components/apps/SmartAppStep";
import { Alert, Grid, Box, Button, Chip, CircularProgress, Paper, Typography, Tabs, Tab, FormControlLabel, FormGroup, Checkbox, Switch, Stack, Tooltip, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Article, CompareArrows, ManageSearch, Restore } from "@mui/icons-material";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import RunOcrServiceButton from "../../components/buttons/run-ocr-service-button/RunOcrServiceButton";
import Player from "../../components/video-player/Player";
import VideoPlayer from "e.sic-platform.webclient/src/components/video-player/VideoPlayer";
import OcrInput from "./components/OcrInput";
import InspectionReport from 'e.sic-platform.webclient/src/components/inspection/InspectionReport';
import ProcessProgressTracker, { ProcessStep, ProcessState } from 'e.sic-platform.webclient/src/components/inspection/ProcessProgressTracker';
import SmartAppBase from "../../components/apps/SmartAppBase";
import LoadingSpinner from "e.sic-platform.webclient/src/components/loading-spinner/LoadingSpinner";
import { FileStates } from "e.sic-platform.webclient/src/services/shop/ShopHistoryStorage";
import { Assignment } from "@mui/icons-material";
import useThemeService from 'e.sic-platform.webclient/src/services/theme-service/ThemeService';
import ExportMenuButton, { ExportGroup } from "../../components/buttons/app-action-buttons/ExportMenuButton";
import DownloadMetadataButton from "../../components/buttons/app-action-buttons/DownloadMetadataButton";
import RestoreButton from "../../components/buttons/app-action-buttons/RestoreButton";
import { isRestoreInFlight } from "../../services/restore/restoreState";
import { ocrApp, inspectionVideoAnalysis, inspectionSegmentation, inspectionReview } from "../../services/apps/SmartApps";
import VaDetectionList from "e.sic-platform.webclient/src/components/inspection/VaDetectionList";
import SaveListButton from "e.sic-platform.webclient/src/components/inspection/SaveListButton";
import SavingBackdrop from "e.sic-platform.webclient/src/components/inspection/SavingBackdrop";
import VideoTools from "e.sic-platform.webclient/src/pages/analysis/components/video-analysis-diagram/tools/VideoTools";
import SegmentationCanvasOverlay, { useFilteredSegmentations } from "e.sic-platform.webclient/src/components/video-player/SegmentationCanvasOverlay";
import HighlightSettings from "e.sic-platform.webclient/src/components/video-player/components/HighlightSettings";
import { AnalysisConfidenceStorageModel } from "e.sic-platform.webclient/src/services/organization-analysis-confidence/AnalysisConfidenceStorage";
import { api } from "e.sic-platform.webclient/src/services/api/ApiService";
import { videoPlayerService } from "e.sic-platform.webclient/src/services/video-player/VideoPlayerService";
import VideoAnalysisDiagramSection from "../video-analysis/components/VideoAnalysisDiagramSection";
import RunVideoAnalysisButton from "../../components/buttons/run-video-analysis-button/RunVideoAnalysisButton";
import InspectionComparison from "e.sic-platform.webclient/src/components/inspection/InspectionComparison";
import AnalysisInspectionGraph from "e.sic-platform.webclient/src/components/inspection/AnalysisInspectionGraph";
import DetectionSourceSelector from "e.sic-platform.webclient/src/components/inspection/DetectionSourceSelector";
import DetectionSourceComparison from "e.sic-platform.webclient/src/components/inspection/DetectionSourceComparison";
import { isApprovedRow, useApprovedList } from "e.sic-platform.webclient/src/components/inspection/useApprovedList";
import { stableKeyFor } from "e.sic-platform.webclient/src/components/inspection/detectionAssessment";
import {
  AnalysisDetection,
  DetectionSource,
  analysisToReviewedDetections,
  buildAnalysisDetections,
  interpolatedDistanceAtTime,
  distanceTrack,
  ocrToAnalysisDetections,
  reviewedToAnalysisDetections,
} from "e.sic-platform.webclient/src/components/inspection/analysisDetections";
import { analysisObservationText } from "e.sic-platform.webclient/src/components/inspection/analysisObservationText";
import { withAnalysisHeaderDefaults } from "e.sic-platform.webclient/src/components/inspection/analysisMainInformationDefaults";
import { ReviewedDetectionDto } from "e.sic-platform.webclient/src/services/ocr/InspectionReviewService";
import {
  ocrTsToSeconds,
  secondsToOcrTimestamp,
} from "e.sic-platform.webclient/src/components/inspection/playbackSync";
import { MainInfoTable } from "e.sic-platform.webclient/src/components/inspection/MainInfoTable";
import { InspectionInsightDetails } from "e.sic-platform.webclient/src/components/inspection/InspectionInsightDetails";
import { mainInfoStorage } from "e.sic-platform.webclient/src/services/ocr/MainInfoStorageModel";
import { inspectionInfoStorage } from "e.sic-platform.webclient/src/services/ocr/InspectionInfoStorageModel";
import { ConfidenceOptions } from "e.sic-platform.webclient/src/pages/analysis/components/video-analysis-diagram/tools/Tools";
import LabelingInfo from "e.sic-platform.webclient/src/components/labeling/LabelingInfo";
import { PubSubEvents } from "e.sic-platform.webclient/src/services/PubSubEvents";
import { creditBalanceStorage } from "e.sic-platform.webclient/src/services/shop/CreditBalanceStorage";
import { snackbarNotificationsService } from "e.sic-platform.webclient/src/services/notifications/SnackbarNotificationsService";
import { shopHistoryStorage } from "e.sic-platform.webclient/src/services/shop/ShopHistoryStorage";
import { orderBreakdownStorage } from "e.sic-platform.webclient/src/services/shop/OrderBreakdownStorage";
import { activityPricesStorage, ActivityTypes } from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import { uploadFilesService } from "e.sic-platform.webclient/src/services/upload/UploadFilesService";
import RedoButton from "../../components/buttons/app-action-buttons/RedoButton";
import WarningBeforeAction from "e.sic-platform.webclient/src/components/data-components/WarningBeforeAction";
import OrderOverview from "../../components/apps/OrderOverview";
import OrderProcessSummary from "./components/OrderProcessSummary";
import OrderAndLabelsPanel from "./components/OrderAndLabelsPanel";
import ServiceOverviewPanel from "./components/ServiceOverviewPanel";
import InspectionInsightsDescription from "./components/InspectionInsightsDescription";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PubSub from "pubsub-js";

// The services the user can pick to run (new or redo) on the Inspection Insights page.
type ServiceKey = "ocr" | "analysis" | "segmentation";

export default observer(function OcrServicePage() {
  const ocrService = ocrApp.services.ocrService!;
  const {themeClass} = useThemeService();
  const navigate = useNavigate();
  const [resultTab, setResultTab] = useState(0);
  // This single "one-page" app offers all three services — Inspection Insights (OCR), Video
  // Analysis, and Segmentation. Inspection Insights and Video Analysis are PRIMARY services
  // (each selectable alone or together); Segmentation is an ADD-ON that may only run alongside
  // a primary — it can never be selected on its own. Valid combos: OCR / Analysis / OCR+Seg /
  // Analysis+Seg / OCR+Analysis / OCR+Analysis+Seg. It is intentionally NOT gated by the
  // per-service Enable_* feature flags (those still gate the standalone apps, the left menu,
  // and the home cards). No per-option price is shown here; the combined price is resolved from
  // the DB (getPrices) and shown in the dialog after selection. OCR is checked by default; the
  // user can check/uncheck any primary, and Segmentation is only enabled while a primary is on.
  const [selection, setSelection] = useState<ServiceKey[]>(["ocr"]);

  // A selected service runs.
  const runOcr = selection.includes("ocr");
  const runAnalysis = selection.includes("analysis");
  const runSegmentation = selection.includes("segmentation");
  // Segmentation is an add-on: it may only run alongside a primary service (OCR / Analysis).
  const hasPrimary = runOcr || runAnalysis;

  // Selected services as ActivityTypes. Passed to the purchase dialog, which resolves
  // the combined DB price (e.g. Analysis+Segmentation -> VideoAnalysisAndSegmentation,
  // OCR+Analysis -> VideoOcrAndAnalysis) rather than summing individual prices.
  const selectedActivities = [
    ...(runOcr ? [ActivityTypes.VideoOcr] : []),
    ...(runAnalysis ? [ActivityTypes.VideoAnalysis] : []),
    ...(runSegmentation ? [ActivityTypes.HighlightDetection] : []),
  ];

  // Which detections the Video Analysis tab shows: the raw analysis output (the default, so a
  // fresh run opens on something rather than on the empty approved list), the reviewer's
  // approved list, or the two side by side.
  const [detectionSource, setDetectionSource] = useState<DetectionSource>("original");

  // Which pair the Comparison tab shows: the raw machine outputs, or the two approved lists.
  const [comparisonMode, setComparisonMode] = useState<"raw" | "approved">("raw");

  // What the user has actually paid for on this file. Set the moment the purchase dialog is
  // confirmed; while it is set the service checkboxes are frozen, because the charge has
  // already been taken for exactly this set. Adding a service afterwards goes through Redo,
  // which prices and charges the new selection.
  const [paidSelection, setPaidSelection] = useState<ServiceKey[] | null>(null);

  // Custom Redo dialog (the framework one only lists OCR; we offer all three services).
  const [showRedo, setShowRedo] = useState(false);
  const [redoHasBalance, setRedoHasBalance] = useState(true);
  // Confirming the Redo dialog does NOT start anything. It puts the page back into its
  // pre-run state — step 2 with the video, the labeling buttons and the Run button, step 3
  // and its results hidden — so the user can adjust the labeling for the new run first.
  // Nothing is submitted (and no progress appears) until they press Run, exactly as for a
  // file that has never been processed. Previously the confirm fired the jobs immediately,
  // which meant a redo always reused the old labels.
  const [redoEditing, setRedoEditing] = useState(false);
  const redoPrice = activityPricesStorage.getPrices(selectedActivities);
  const selectionKey = selection.join(",");
  // Names the services the quoted price actually covers, so the amount in the Redo dialog is
  // never just a bare number.
  const selectedServiceLabels = [
    ...(runOcr ? [translationService.translate("ocrService.mode.ocr")] : []),
    ...(runAnalysis ? [translationService.translate("ocrService.mode.analysis")] : []),
    ...(runSegmentation ? [translationService.translate("ocrService.mode.segmentation")] : []),
  ].join(" + ");
  // "Mark damage" option: overlays segmentation polygons on the video + frames.
  const [markDamage, setMarkDamage] = useState(false);
  const [confidenceStorage] = useState(() =>
    AnalysisConfidenceStorageModel.create({ autoLoad: false }, { api })
  );
  // Confidence-filtered masks shared by the video overlay and each detection frame.
  const filteredSegmentations = useFilteredSegmentations(inspectionSegmentation);
  // Driven purely by whether this file has segmentation data (its segmentations.json
  // output) — NOT by any feature flag. If the masks loaded, the option is available.
  // Drives whether the "Mark damage" option is shown at all (segmentation data present).
  const canMarkDamage = inspectionSegmentation.segmentationSuccessful;

  // View vs run mode. A file that already has ANY result (OCR, Analysis, or Segmentation)
  // opens in read-only VIEW mode: show the saved labels + the result tabs, NOT the run
  // controls. The run controls (service checkboxes + Run button + label-and-run video) appear
  // only for a fresh file where nothing has succeeded and nothing is running; re-running is
  // done via the Redo dialog in the action bar. (Previously this keyed off OCR-only flags, so a
  // file processed via Analysis/Segmentation without OCR wrongly showed the run controls.)
  const hasAnyResult =
    ocrService.ocrSuccessful ||
    inspectionVideoAnalysis.analysisSuccessful ||
    inspectionSegmentation.segmentationSuccessful;
  const anyRunning =
    ocrService.ocrRunning ||
    inspectionVideoAnalysis.analysisRunning ||
    inspectionSegmentation.segmentationRunning;
  // While a redo is being set up the existing results must not drive the layout: step 3 and
  // everything in it stays hidden and step 2 goes back to its editable run state, so the page
  // looks and behaves like a file that has not been processed yet.
  const showResults = hasAnyResult && !redoEditing;
  const showRunControls = !showResults && !anyRunning;
  // A run is in flight and nothing has come back yet. Step 3 is mounted for this, but it shows
  // the run's state instead of the report: the result panels have nothing to draw, so they
  // would render an empty report — "There is no Main Information available", an empty
  // detection list — next to the result video, which reads as a finished run that found
  // nothing. Keyed on hasAnyResult, so a partial result (e.g. Inspection done, Analysis still
  // going) brings the real content back immediately. A staged redo has started nothing, so it
  // is not pending either — its old results are simply hidden.
  const resultsPending = anyRunning && !hasAnyResult && !redoEditing;
  // Loading state for an OPENED file (an existing file's data being fetched). The three
  // services (OCR + the two standalone VA/segmentation ones) each set isLoading around their
  // own async load(), so this is true while ANY fetch is in flight.
  const anyLoading =
    ocrService.isLoading ||
    inspectionVideoAnalysis.isLoading ||
    inspectionSegmentation.isLoading;
  // Opening a file, nothing resolved yet -> show a spinner instead of transiently flashing the
  // fresh-file label/run controls (which appear because hasAnyResult/anyRunning are momentarily
  // false for a not-yet-loaded analysis/segmentation-only file).
  const isInitiallyLoading = anyLoading && !hasAnyResult && !anyRunning;
  // A result is already visible but more fetches are still in flight (e.g. Inspection loaded,
  // Video Analysis still coming) -> show a small "loading more" indicator next to the tabs so
  // the user knows additional tabs are on the way.
  const isLoadingMore = anyLoading && hasAnyResult;

  // Run an orchestration without letting a failed request escape as an unhandled promise
  // rejection. ApiService already reports the failure in a snackbar; letting the rejection
  // through additionally trips the dev overlay, which renders it as an unhelpful crash screen
  // on top of the message the user has already been given.
  const runGuarded = useCallback(async (what: string, action: () => Promise<void>) => {
    try {
      await action();
    } catch (error) {
      console.error(`${what} failed:`, error);
    }
  }, []);

  // Start a (paid) Video Analysis for the same file from the "Video Analysis"
  // tab. The reused VideoAnalysisDiagram only offers a run action on the failed
  // state, so we provide our own button for the not-started state.
  const startVideoAnalysis = useCallback(async () => {
    await inspectionVideoAnalysis.startService();
    await inspectionVideoAnalysis.load();
    await creditBalanceStorage.load();
    await shopHistoryStorage.load();
  }, [])

  // Re-run a single service that FAILED, from the Order & Process panel. A failed run left no
  // charge (the backend charges on success only), so this simply re-triggers that one service;
  // OCR re-uses its already-saved labels. Refresh balance + the as-charged order rows after.
  const rerunService = useCallback(async (key: ServiceKey) => {
    if (key === "ocr") {
      if (!ocrService.inputProvided) return;
      await ocrService.redo();
      await ocrService.startService();
      await ocrService.load();
    } else if (key === "analysis") {
      await inspectionVideoAnalysis.startService();
      await inspectionVideoAnalysis.load();
    } else if (key === "segmentation") {
      await inspectionSegmentation.startService();
      await inspectionSegmentation.load();
    }
    await creditBalanceStorage.load();
    await shopHistoryStorage.load();
    if (ocrService.fileId !== undefined) await orderBreakdownStorage.loadForFile(ocrService.fileId);
  }, [ocrService]);

  // Run whatever the user selected. For "both" the OCR and Video Analysis runs
  // are kicked off in parallel; each result then lands in its own tab as soon as
  // that process finishes (handled by the per-service completion effects below).
  // Which file's segmentation this session has already sent. Segmentation is submitted exactly
  // ONCE per run, by whichever of the two paths is the first legitimate moment: the Run handlers
  // when there is no analysis to wait for, otherwise the effect that fires on analysisSuccessful.
  // This latch is what keeps that "exactly once" true across the two.
  const segmentationDispatchedFor = useRef<number | undefined>(undefined);
  // Which file the user has actually LAUNCHED a segmentation for (Run on a fresh file, or Run
  // after a staged Redo). The deferred-dispatch effect below keys on THIS, never on the
  // Segmentation checkbox: ticking the box in the Redo dialog only expresses an intent and
  // must submit (and charge) nothing until the run is confirmed and started. Previously the
  // effect treated `runSegmentation` itself as "wanted", so on a file whose analysis had
  // already succeeded, checking Segmentation in the Redo dialog fired the job immediately --
  // before the dialog was even confirmed.
  const segmentationArmedFor = useRef<number | undefined>(undefined);

  const startSelected = useCallback(async () => {
    // OCR runs FIRST when selected: its trigger persists the labeled ROIs (incl. the
    // distance area) which the analysis run then reuses to detect distance. Once the
    // labels exist, analysis + segmentation fire in parallel. A service is (re)triggered
    // only when not already running/successful (stale "Failed" re-runs; a paid success
    // isn't re-charged).
    if (runOcr && ocrService.inputProvided && !ocrService.ocrRunning && !ocrService.ocrSuccessful) {
      await ocrService.startService();
    } else if ((runAnalysis || runSegmentation) && ocrService.inputProvided) {
      // OCR isn't being (re)run this cycle (deselected, or already succeeded), but labeling is
      // mandatory for every run. Persist the labels first (no OCR job, no charge) so Analysis
      // (BuildOcrInputFromLabels) AND Segmentation get the labeled ROIs incl. the distance area.
      await ocrService.saveLabels();
    }

    // Analysis and segmentation of ONE file cannot be submitted to the Job Manager at the
    // same time — they collide on its shared task ID ("{JobId}_{second}_{MD5(fileUri)}", no
    // analysis/segmentation marker) with "There is running task with the same name". So fire
    // analysis FIRST and AWAIT it: that persists VideoAutoDetectionIsInProgress before
    // segmentation's trigger runs, so the backend defers segmentation and its analysis-success
    // callback submits it. (Segmentation is only selectable alongside analysis; if analysis
    // already succeeded it's skipped here and segmentation submits directly — no collision.)
    // Does this cycle include an analysis that segmentation has to wait for? Captured BEFORE the
    // analysis trigger below, which flips the store to running.
    const waitingForAnalysis =
      inspectionVideoAnalysis.analysisRunning ||
      (runAnalysis && !inspectionVideoAnalysis.analysisSuccessful);

    // A run marked in progress with no backend start is QUEUED, not running: it was requested
    // and its Job Manager submission was deferred (or lost). Re-triggering it is how it finally
    // gets sent — skipping it, as "already running" did, leaves it waiting for good.
    const analysisQueued =
      inspectionVideoAnalysis.analysisRunning && !inspectionVideoAnalysis.startedAt;
    const segmentationQueued =
      inspectionSegmentation.segmentationRunning && !inspectionSegmentation.startedAt;
    if (
      runAnalysis &&
      !inspectionVideoAnalysis.analysisSuccessful &&
      (!inspectionVideoAnalysis.analysisRunning || analysisQueued)
    )
      await inspectionVideoAnalysis.startService();

    // The run is live now, so segmentation may be submitted for this file - by this handler or,
    // when it has to wait for the analysis above, by the effect that fires on analysisSuccessful.
    if (runSegmentation) segmentationArmedFor.current = Number(inspectionSegmentation.fileId);

    // Segmentation is submitted ONCE, at the first moment nothing else holds the Job Manager's
    // shared task ID: here when this cycle has no analysis, otherwise by the analysisSuccessful
    // effect below. Sending it here as well would submit the same run twice.
    if (
      runSegmentation &&
      !waitingForAnalysis &&
      !inspectionSegmentation.segmentationSuccessful &&
      (!inspectionSegmentation.segmentationRunning || segmentationQueued)
    ) {
      segmentationDispatchedFor.current = Number(inspectionSegmentation.fileId);
      await inspectionSegmentation.startService();
    }

    const loads = [];
    if (runOcr) loads.push(ocrService.load());
    if (runAnalysis) loads.push(inspectionVideoAnalysis.load());
    if (runSegmentation) loads.push(inspectionSegmentation.load());
    await Promise.all(loads);

    await creditBalanceStorage.load();
    await shopHistoryStorage.load();
  }, [runOcr, runAnalysis, runSegmentation, ocrService])

  // Confirm of the upload-step purchase dialog (new file). OCR always runs via the
  // standard upload pipeline; remember the optional Analysis/Segmentation add-ons so
  // they fire once the uploaded video has finished converting (effect below).
  const startNewUpload = useCallback(() => {
    // The charge is taken for exactly this set — freeze it so the run that follows cannot
    // silently differ from what was billed.
    setPaidSelection(selection);
    if (runOcr) {
      // OCR selected: OcrService.runAfterUpload auto-runs OCR (preparing). Analysis &
      // Segmentation are NOT pre-fired here — they start at the Run click (startSelected),
      // AFTER OCR persists the labeled ROIs, so the analysis run receives the distance
      // region. Pre-firing them at upload (before any labeling exists) would skip distance.
      ocrApp.upload();
    } else {
      // OCR deselected (Analysis / Analysis+Segmentation): labeling is mandatory for every
      // run, so we DON'T auto-fire the AI on upload anymore. Just land on the file in the
      // not-started state — the page shows the video + labeling UI, and the user labels then
      // clicks Run (startSelected), which persists the labels (saveLabels) before analysis.
      uploadFilesService.startStandaloneUpload(async (file: any) => {
        file.completed();
        ocrApp.setFileId(file.id);
        ocrApp.setFileGuid(await ocrApp.getFileGuid(file.id));
        await ocrApp.load();
        await creditBalanceStorage.load();
        ocrApp.setNavigate(ocrApp.fileRoute);
      }, (ocrApp as any).uploadAllFileTypes);
    }
  }, [runOcr, runAnalysis, runSegmentation, selection]);

  // Re-run the selected services from the Redo dialog. OCR is reset then re-run; Analysis
  // and Segmentation are re-triggered directly. Only the checked services are touched.
  const redoSelected = useCallback(async () => {
    // OCR re-runs FIRST (when its labels are present): its trigger re-persists the labeled
    // ROIs, which the analysis re-run then reuses for distance. We AWAIT it (rather than
    // adding it to the parallel batch) so the labels are stored before analysis's
    // TriggerVideoAnalysis reads them — otherwise the re-run races the label rewrite
    // (AddOcrLabels deletes-then-re-adds) and analysis gets no distance. Gating on
    // inputProvided also prevents the null-coordinate crash in runService (mirrors the
    // Run button being disabled when !inputProvided).
    if (runOcr && ocrService.inputProvided) {
      await ocrService.redo();
      await ocrService.startService();
    } else if ((runAnalysis || runSegmentation) && ocrService.inputProvided) {
      // OCR not part of this redo, but labeling is mandatory for every run. Re-persist the
      // (reused) labels so the Analysis and/or Segmentation re-run reads them. The labels were
      // loaded back into the dialogs when the file opened, so the user reuses them as-is.
      await ocrService.saveLabels();
    }

    // Sequence analysis before segmentation (see startSelected): they can't be submitted to
    // the Job Manager together for the same file without colliding on its shared task ID.
    // Awaiting analysis first lets the backend defer segmentation until analysis completes.
    const waitingForAnalysis =
      inspectionVideoAnalysis.analysisRunning ||
      (runAnalysis && !inspectionVideoAnalysis.analysisSuccessful);
    if (runAnalysis) await inspectionVideoAnalysis.startService();
    // The redo is live now, so segmentation may be submitted for this file (see startSelected).
    if (runSegmentation) segmentationArmedFor.current = Number(inspectionSegmentation.fileId);
    // Held back until analysis succeeds when analysis is part of the redo (see startSelected).
    if (runSegmentation && !waitingForAnalysis) {
      segmentationDispatchedFor.current = Number(inspectionSegmentation.fileId);
      await inspectionSegmentation.startService();
    }

    const loads = [];
    if (runOcr) loads.push(ocrService.load());
    if (runAnalysis) loads.push(inspectionVideoAnalysis.load());
    if (runSegmentation) loads.push(inspectionSegmentation.load());
    await Promise.all(loads);

    await creditBalanceStorage.load();
    await shopHistoryStorage.load();
  }, [runOcr, runAnalysis, runSegmentation]);

  // The Run click while a redo is staged. Leaving edit mode BEFORE the triggers is deliberate:
  // OcrService.redo() clears the previous OCR result synchronously inside redoSelected, so the
  // results step comes back already reset, showing the progress tracker rather than a flash of
  // the outgoing report.
  const runStagedRedo = useCallback(async () => {
    setRedoEditing(false);
    await redoSelected();
  }, [redoSelected]);

  // Re-validate balance against the selected services whenever the dialog opens or the
  // selection changes, so the REDO button + warning reflect what will actually run.
  useEffect(() => {
    if (!showRedo) return;
    creditBalanceStorage.validateRemainingBalance(selectedActivities).then((r) => setRedoHasBalance(!!r));
  }, [showRedo, selectionKey]);

  // A staged redo belongs to the file it was started on — opening another file drops it,
  // otherwise that file would open straight into the label-and-run state. The armed
  // segmentation belongs to that run too, so it goes with it.
  useEffect(() => {
    setRedoEditing(false);
    segmentationArmedFor.current = undefined;
  }, [ocrService.fileId]);

  // "New" throws the file away, but this page's own state lives in React and AppStorage.new()
  // cannot reach it — so the previous order stayed set up on a page that no longer had a file:
  // the service checkboxes still showed that file's PAID selection and, because paidSelection
  // was still set, showed it LOCKED, with the "paid for this selection" notice under it. The
  // upload step was therefore unusable for anything but a repeat of the last order.
  //
  // Reset everything the open file put here as soon as it goes away, so "New" lands on exactly
  // the state a first visit does: Inspection Insights checked, the other two clear, all three
  // enabled. Keyed on the file id, so confirming a purchase (which sets paidSelection while the
  // id is still undefined, just before the upload) does not undo itself — the effect only runs
  // when the id actually changes.
  useEffect(() => {
    if (ocrService.fileId !== undefined) return;
    setPaidSelection(null);
    setSelection(["ocr"]);
    setShowRedo(false);
    setResultTab(0);
    setDetectionSource("original");
    setComparisonMode("raw");
    setMarkDamage(false);
  }, [ocrService.fileId]);

  // (Labeling is mandatory for every run, so there is no "auto-fire add-ons on upload"
  // path anymore — Analysis/Segmentation always start from the Run click after labeling.)

  // The service selector — plain checkbox labels, NO prices shown here. OCR is selected by
  // default but freely deselectable; Segmentation is an add-on, only selectable while a primary
  // (Inspection Insights or Video Analysis) is on. The combined DB price is shown afterwards in
  // the dialog.
  const toggleService = (key: ServiceKey, checked: boolean) => {
    setSelection((prev) => {
      const next = checked ? [...prev, key] : prev.filter((k) => k !== key);
      // Segmentation can't stand alone — if no primary (OCR/Analysis) remains, drop it too.
      const primaryLeft = next.includes("ocr") || next.includes("analysis");
      return primaryLeft ? next : next.filter((k) => k !== "segmentation");
    });
  };
  // `locked` freezes the selection to what the user has already paid for. Changing it after
  // payment would silently run a service that was never charged (or skip one that was), so
  // the only way to add a service to a paid file is Redo — which prices and charges again.
  const renderServiceSelector = (locked = false) => (
    <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 1 }}>
      <Tooltip title={locked ? translationService.translate("ocrService.mode.lockedHint") : ""}>
        <FormGroup row>
          {/* Inspection Insights & Video Analysis are primaries (any mix); Segmentation is an add-on. */}
          <FormControlLabel
            control={<Checkbox checked={runOcr} disabled={locked} onChange={(e) => toggleService("ocr", e.target.checked)} />}
            label={translationService.translate("ocrService.mode.ocr")}
          />
          <FormControlLabel
            control={<Checkbox checked={runAnalysis} disabled={locked} onChange={(e) => toggleService("analysis", e.target.checked)} />}
            label={translationService.translate("ocrService.mode.analysis")}
          />
          {/* Segmentation is an add-on: disabled until a primary (OCR/Analysis) is selected. */}
          <Tooltip title={locked || hasPrimary ? "" : translationService.translate("ocrService.mode.segmentationHint")}>
            <span>
              <FormControlLabel
                control={<Checkbox checked={runSegmentation} disabled={locked || !hasPrimary} onChange={(e) => toggleService("segmentation", e.target.checked)} />}
                label={translationService.translate("ocrService.mode.segmentation")}
              />
            </span>
          </Tooltip>
        </FormGroup>
      </Tooltip>
      {locked && (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
          {translationService.translate("ocrService.mode.lockedNotice")}
        </Typography>
      )}
    </Grid>
  );

  // The upload step's version of the selector: the services stacked, each with what it does
  // under its checkbox, next to an example frame for the current choice. Same rules as
  // renderServiceSelector (Segmentation needs a primary; a paid selection is locked).
  const renderServiceChoice = (locked = false) => {
    const services: { key: ServiceKey; checked: boolean; disabled: boolean; descriptionKey: string }[] = [
      { key: "ocr", checked: runOcr, disabled: locked, descriptionKey: "ocrService.info.ocr" },
      { key: "analysis", checked: runAnalysis, disabled: locked, descriptionKey: "ocrService.info.analysis" },
      { key: "segmentation", checked: runSegmentation, disabled: locked || !hasPrimary, descriptionKey: "ocrService.info.segmentation" },
    ];
    return (
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Tooltip title={locked ? translationService.translate("ocrService.mode.lockedHint") : ""}>
            <Stack spacing={1}>
              {services.map(({ key, checked, disabled, descriptionKey }) => (
                <Tooltip
                  key={key}
                  title={key === "segmentation" && !locked && !hasPrimary
                    ? translationService.translate("ocrService.mode.segmentationHint")
                    : ""}
                >
                  <Box>
                    <FormControlLabel
                      sx={{ mb: 0 }}
                      control={<Checkbox checked={checked} disabled={disabled} onChange={(e) => toggleService(key, e.target.checked)} />}
                      label={translationService.translate(`ocrService.mode.${key}`)}
                    />
                    <Typography variant="body2" color="GrayText" sx={{ ml: 0.5 }}>
                      {translationService.translate(descriptionKey)}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Tooltip>
          {locked && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {translationService.translate("ocrService.mode.lockedNotice")}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <ServiceOverviewPanel selection={selection} />
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    ocrApp.toggleCollapsed(true)
  }, []);

  // Stable VideoOcrFinished subscription at page level (survives REDO state
  // transitions and conditional rendering of step 3, unlike the one inside
  // InspectionReport whose useEffect re-runs on every ocr.status change).
  // When the SignalR event for our fileId arrives, refresh the OCR data.
  useEffect(() => {
    const token = PubSub.subscribe(
      PubSubEvents.VideoOcrFinished,
      async (_event: string, notification: any) => {
        const finishedId = Number(notification?.data?.fileId);
        const currentId = Number(ocrService.fileId);
        if (Number.isFinite(finishedId) && finishedId === currentId) {
          await (ocrService as any).load();
          // Also refresh navbar balance and global shop-history so the
          // left-menu badge count updates.
          await creditBalanceStorage.load();
          await shopHistoryStorage.load();
        }
      }
    );
    return () => {
      PubSub.unsubscribe(token);
    };
  }, [ocrService]);

  // OCR-status polling fallback: while a run is in flight (status === Running),
  // poll the backend every 10 seconds. If the SignalR event ever drops, the
  // poller catches the completion and refreshes the page on its own.
  useEffect(() => {
    if (!ocrService.ocrRunning) return;
    const interval = window.setInterval(() => {
      void (ocrService as any).checkStatus?.();
    }, 10000);
    return () => window.clearInterval(interval);
  }, [ocrService.ocrRunning, ocrService]);

  // Refresh navbar balance + shop-history when an OCR run actually COMPLETES — i.e. it
  // transitions running -> success/failed — NOT every time these flags read true for an
  // already-processed file that is merely being OPENED. (Without the wasRunning guard, opening
  // a completed file re-fetched balance+history once per service = 3x for nothing.) The
  // VideoOcrFinished handler covers the SignalR path; this covers the poll fallback.
  const ocrWasRunning = useRef(false);
  useEffect(() => {
    if (ocrService.ocrRunning) { ocrWasRunning.current = true; return; }
    if ((ocrService.ocrSuccessful || ocrService.ocrFailed) && ocrWasRunning.current) {
      ocrWasRunning.current = false;
      void creditBalanceStorage.load();
      void shopHistoryStorage.load();
    }
  }, [ocrService.ocrRunning, ocrService.ocrSuccessful, ocrService.ocrFailed]);

  // Mirror of the OCR completion handling, for the (parallel) Video Analysis run
  // so its result lands independently in the Video Analysis / Comparison tabs.
  // SignalR event for our fileId -> refresh analysis data + navbar/menu.
  useEffect(() => {
    const token = PubSub.subscribe(
      PubSubEvents.VideoAnalysisFinished,
      async (_event: string, notification: any) => {
        const finishedId = Number(notification?.data?.fileId);
        const currentId = Number(inspectionVideoAnalysis.fileId);
        if (Number.isFinite(finishedId) && finishedId === currentId) {
          await (inspectionVideoAnalysis as any).load();
          await creditBalanceStorage.load();
          await shopHistoryStorage.load();
        }
      }
    );
    return () => {
      PubSub.unsubscribe(token);
    };
  }, []);

  // Polling fallback: while an analysis run is in flight, reload every 10s so a
  // dropped SignalR event still surfaces the result. AnalysisService has no
  // checkStatus; load()->request() re-reads the status from the backend.
  useEffect(() => {
    if (!inspectionVideoAnalysis.analysisRunning) return;
    const interval = window.setInterval(() => {
      void (inspectionVideoAnalysis as any).load();
    }, 10000);
    return () => window.clearInterval(interval);
  }, [inspectionVideoAnalysis.analysisRunning]);

  // Same wasRunning guard as OCR: refresh balance/history only when analysis actually
  // completes (running -> done), not when opening an already-analyzed file.
  const analysisWasRunning = useRef(false);
  useEffect(() => {
    if (inspectionVideoAnalysis.analysisRunning) { analysisWasRunning.current = true; return; }
    if ((inspectionVideoAnalysis.analysisSuccessful || inspectionVideoAnalysis.analysisFailed) && analysisWasRunning.current) {
      analysisWasRunning.current = false;
      void creditBalanceStorage.load();
      void shopHistoryStorage.load();
    }
  }, [inspectionVideoAnalysis.analysisRunning, inspectionVideoAnalysis.analysisSuccessful, inspectionVideoAnalysis.analysisFailed]);

  /**
   * Send a QUEUED segmentation as soon as analysis has SUCCEEDED.
   *
   * The two collide on the Job Manager's shared task ID, so segmentation is never submitted
   * alongside a running analysis: its trigger only MARKS it and the backend defers the
   * submission. What normally sends it afterwards is the analysis-success callback
   * (VideoProcessingFinalizer.FinalizeSuccessfulAnalysis) — but that callback is served by
   * whichever API is DEPLOYED at the reply URL, so wherever the deployed build predates that
   * dispatch, the run stays queued forever and the user sees "Waiting" with nothing coming.
   *
   * This page learns the same fact from its own polling, so it dispatches too: once analysis
   * reads successful and segmentation is still queued — in progress with no backend start,
   * meaning it was never handed to the Job Manager — trigger it. The backend's queued
   * exemption lets that call through and submits it for real.
   *
   * It cannot double-submit: a run that WAS sent carries a start time, so it is not queued and
   * this skips it. And because the condition is read from the backend rather than remembered
   * client-side, it also recovers a file whose dispatch was lost before this existed — opening
   * the page is enough.
   */
  useEffect(() => {
    const fileId = Number(inspectionSegmentation.fileId);

    // Already sent (a backend start means it reached the Job Manager) or already finished:
    // nothing to do, and the latch is released so a later re-run is dispatched in its turn.
    const alreadySubmitted =
      inspectionSegmentation.segmentationRunning && !!inspectionSegmentation.startedAt;
    if (alreadySubmitted || inspectionSegmentation.segmentationSuccessful) {
      segmentationDispatchedFor.current = undefined;
      segmentationArmedFor.current = undefined;
      return;
    }

    // Wanted either because the user STARTED a run that includes it (Run / Run after a staged
    // Redo armed it for this file), or because the backend still has it marked from a run whose
    // submission never happened - that second case is what lets simply opening the page recover
    // a file left waiting. Deliberately NOT keyed on `runSegmentation`: the checkbox is only an
    // intent, and reading it here submitted the job the moment it was ticked in the Redo dialog.
    const wanted =
      segmentationArmedFor.current === fileId ||
      (inspectionSegmentation.segmentationRunning && !inspectionSegmentation.startedAt);
    if (!wanted) {
      segmentationDispatchedFor.current = undefined;
      return;
    }

    // Still waiting on the analysis that holds the task ID.
    if (!inspectionVideoAnalysis.analysisSuccessful) return;
    if (!Number.isFinite(fileId) || segmentationDispatchedFor.current === fileId) return;

    segmentationDispatchedFor.current = fileId;
    void (async () => {
      await inspectionSegmentation.startService();
      await (inspectionSegmentation as any).load();
    })();
  }, [
    inspectionVideoAnalysis.analysisSuccessful,
    inspectionSegmentation.fileId,
    inspectionSegmentation.segmentationRunning,
    inspectionSegmentation.startedAt,
    inspectionSegmentation.segmentationSuccessful,
  ]);

  // Mirror of the analysis completion handling for the (now runnable) Segmentation
  // service: when the SignalR event for our fileId arrives, reload the masks so the
  // "Mark damage" overlay lights up, and refresh the navbar balance + shop history.
  useEffect(() => {
    const token = PubSub.subscribe(
      PubSubEvents.VideoSegmentationFinished,
      async (_event: string, notification: any) => {
        const finishedId = Number(notification?.data?.fileId);
        const currentId = Number(inspectionSegmentation.fileId);
        if (Number.isFinite(finishedId) && finishedId === currentId) {
          await (inspectionSegmentation as any).load();
          await creditBalanceStorage.load();
          await shopHistoryStorage.load();
        }
      }
    );
    return () => {
      PubSub.unsubscribe(token);
    };
  }, []);

  // Polling fallback while a segmentation run is in flight (mirrors the analysis one).
  useEffect(() => {
    if (!inspectionSegmentation.segmentationRunning) return;
    const interval = window.setInterval(() => {
      void (inspectionSegmentation as any).load();
    }, 10000);
    return () => window.clearInterval(interval);
  }, [inspectionSegmentation.segmentationRunning]);

  // Same wasRunning guard as OCR/analysis: refresh balance/history only when segmentation
  // actually completes (running -> done), not when opening an already-segmented file.
  const segmentationWasRunning = useRef(false);
  useEffect(() => {
    if (inspectionSegmentation.segmentationRunning) { segmentationWasRunning.current = true; return; }
    if ((inspectionSegmentation.segmentationSuccessful || inspectionSegmentation.segmentationFailed) && segmentationWasRunning.current) {
      segmentationWasRunning.current = false;
      void creditBalanceStorage.load();
      void shopHistoryStorage.load();
    }
  }, [inspectionSegmentation.segmentationRunning, inspectionSegmentation.segmentationSuccessful, inspectionSegmentation.segmentationFailed]);

  // Default the result tab to whatever the user chose to run. When Analysis is the
  // only selected service, land on the Video Analysis tab; otherwise the Inspection tab.
  useEffect(() => {
    setResultTab(runAnalysis && !runOcr ? 1 : 0);
  }, [runAnalysis, runOcr]);

  // Default the analysis confidence to Overall @ 0.65 on EVERY (re)load of the analysis
  // data — NOT once per file. AnalysisService.request() resets analysisOverallConfidence to
  // the file's recommended level (e.g. 0.9) on each load, and the page mounts/refreshes the
  // analysis service several times (initial load, then the run-completion SignalR reload and
  // the poll fallback). With a once-per-file guard, that second load silently bumped the
  // confidence to 90% and never restored 0.65 — which drops the RoI detections (they only
  // exist at the lower threshold) and with them the Distance column, leaving bare per-second
  // predictions with "—". Keying off vaData re-asserts 0.65 whenever the data actually
  // reloads; a normal re-render (or the user dragging the slider) doesn't change vaData, so
  // the user can still raise the threshold and it stays until the next real reload.
  useEffect(() => {
    if (inspectionVideoAnalysis.analysisSuccessful) {
      // Overall @ the lowest threshold (0.65) so the inspection graph/table shows detections
      // on load. Higher defaults were tried and left the table empty ("No rows") on files
      // whose recommended Overall is high with no detections at that level.
      inspectionVideoAnalysis.setAnalysisConfidenceOption(ConfidenceOptions.Overall);
      inspectionVideoAnalysis.setAnalysisOverallConfidence(0.65);
    }
  }, [
    inspectionVideoAnalysis.analysisSuccessful,
    inspectionVideoAnalysis.fileId,
    inspectionVideoAnalysis.vaData,
  ]);

  // Keep the app-level fileId in sync with the loaded OCR service file. The sticky
  // header bar's "Copy URL" button is gated on `ocrApp.fileId`; without this it never
  // appears after load on this page (only the service's fileId gets set otherwise).
  useEffect(() => {
    if (ocrService.fileId !== undefined && ocrApp.fileId !== ocrService.fileId) {
      ocrApp.setFileId(ocrService.fileId);
    }
  }, [ocrService.fileId]);

  // Load the human review for the current file. Keyed on the service COMPLETIONS too: a
  // redo marks the review Stale server-side, so the chip must refresh once the run finishes
  // rather than showing an approval that no longer describes the results.
  useEffect(() => {
    inspectionReview.setFileId(ocrService.fileId);
    if (ocrService.fileId === undefined) return;
    void inspectionReview.load();
  }, [
    ocrService.fileId,
    ocrService.ocrSuccessful,
    inspectionVideoAnalysis.analysisSuccessful,
    inspectionSegmentation.segmentationSuccessful,
  ]);

  // Lay the reviewer's main-information values over the extracted ones.
  //
  // OcrService re-reads ocr.json into mainInfoStorage on every load, and the review is fetched
  // separately, so whichever lands second would otherwise win: a corrected header survived in
  // the review and in the exports but vanished from the screen on refresh. Keyed on the review's
  // own snapshot so it re-runs when the review reloads (after a save, or an approval).
  const reviewMainInfoSnapshot = JSON.stringify(inspectionReview.mainInformation ?? []);
  useEffect(() => {
    if (ocrService.fileId === undefined) return;
    mainInfoStorage.applyReviewMainInformation(inspectionReview.mainInformation ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocrService.fileId, ocrService.ocrSuccessful, reviewMainInfoSnapshot]);

  // (The approved OCR rows are mirrored into inspectionInfoStorage further down, once
  // ocrApprovedList exists — they must include the reviewer's UNSAVED edits, which live in
  // that hook rather than in the loaded review.)

  // Load the exact as-charged order breakdown for the current file, and refresh it whenever a
  // service COMPLETES (success/failed) — the credits row only exists after a successful run.
  // Clears on "New"/no file. Backend-sourced, so the panel is correct after a page refresh too.
  useEffect(() => {
    const id = ocrService.fileId;
    if (id === undefined) { orderBreakdownStorage.clear(); return; }
    void orderBreakdownStorage.loadForFile(id);
  }, [
    ocrService.fileId,
    ocrService.ocrSuccessful,
    ocrService.ocrFailed,
    inspectionVideoAnalysis.analysisSuccessful,
    inspectionVideoAnalysis.analysisFailed,
    inspectionSegmentation.segmentationSuccessful,
    inspectionSegmentation.segmentationFailed,
  ]);

  // Load the SAME file's Video Analysis data into the standalone VA service that
  // backs the "Video Analysis" tab. Kept separate from ocrApp.services on purpose
  // (see SmartApps.ts) so opening/uploading to Inspection Insights never auto-runs
  // a paid analysis — the user starts analysis explicitly from the tab.
  useEffect(() => {
    const id = ocrService.fileId;
    // When the file id goes away (e.g. "New"), CLEAR the standalone VA store rather than
    // bailing out. Leaving it populated keeps analysisSuccessful true, which (a) shows stale
    // VA/Comparison tabs for a file that's no longer open and (b) — combined with AppPage
    // forcing step=1 when there's no fileId — is the precondition for the step 1<->3 render
    // loop that froze the page after "New".
    if (id === undefined) {
      (inspectionVideoAnalysis as any).clear();
      // "New" doesn't unmount the page, so also stop any background frame captures here.
      (videoPlayerService as any).abortHiddenCaptures?.();
      return;
    }
    // Reset any state from the previously-viewed file BEFORE loading the new one. Otherwise a
    // stale analysisSuccessful (e.g. navigating from an Analysis file to an OCR-only file)
    // leaves the Video Analysis + Comparison tabs showing until a second load resets them.
    (inspectionVideoAnalysis as any).clear();
    inspectionVideoAnalysis.setFileId(id);
    const guid = (ocrService as any).fileGuid;
    if (guid) inspectionVideoAnalysis.setFileGuid(guid);
    // Swallow load failures (e.g. va-data 401 / file without analysis) so a rejected
    // flow doesn't surface as an uncaught runtime error overlay.
    void (inspectionVideoAnalysis as any)
      .load()
      .catch(() => (inspectionVideoAnalysis as any).setLoading(false));
  }, [ocrService.fileId]);

  // Load the SAME file's segmentation masks into the standalone segmentation service
  // that backs the optional "Mark damage" overlay. Kept out of ocrApp.services on
  // purpose (see SmartApps.ts) so opening Inspection Insights never auto-runs a paid
  // segmentation — we only read existing masks (segmentations.json) for the file.
  useEffect(() => {
    const id = ocrService.fileId;
    // As with analysis above: on "New"/no-file, clear the standalone segmentation store so the
    // "Mark damage" option and segmentationSuccessful don't linger (which would also help sustain
    // the post-"New" render loop).
    if (id === undefined) { (inspectionSegmentation as any).clear(); return; }
    // Reset stale segmentation state from the previous file first (same reason as analysis
    // above), so the "Mark damage" option doesn't linger from a segmentation file.
    (inspectionSegmentation as any).clear();
    inspectionSegmentation.setFileId(id);
    const guid = (ocrService as any).fileGuid;
    if (guid) inspectionSegmentation.setFileGuid(guid);
    // Swallow load failures (e.g. va-data 401 / file without segmentation) so a
    // rejected flow doesn't surface as an uncaught runtime error overlay; the toggle
    // just stays disabled.
    void (inspectionSegmentation as any)
      .load()
      .catch(() => (inspectionSegmentation as any).setLoading(false));
  }, [ocrService.fileId]);

  // Auto-apply the overlay once masks are available for the file (user can still
  // toggle it off). Ref guard keeps it from snapping back on after a manual toggle.
  const markDamageDefaultedFor = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (
      inspectionSegmentation.segmentationSuccessful &&
      markDamageDefaultedFor.current !== inspectionSegmentation.fileId
    ) {
      // Make sure the per-class group codes exist (request() only fills them behind a
      // feature flag); we drive the overlay off data presence, not the flag.
      (inspectionSegmentation as any).ensureDetectedGroupCodes();
      setMarkDamage(true);
      markDamageDefaultedFor.current = inspectionSegmentation.fileId;
    }
  }, [inspectionSegmentation.segmentationSuccessful, inspectionSegmentation.fileId]);

  // Keep the shared videoPlayerService.groupCodeFilterOptions (VideoTools' per-class
  // buttons) pointed at the segmentation service's detected group codes while the
  // overlay is on — the analysis service for the same file may have overwritten them.
  useEffect(() => {
    if (markDamage && inspectionSegmentation.segmentationSuccessful) {
      (inspectionSegmentation as any).ensureDetectedGroupCodes();
      videoPlayerService.setGroupCodesFilterOptions(
        inspectionSegmentation.detectedGroupCodes.slice()
      );
    }
  }, [markDamage, inspectionSegmentation.segmentationSuccessful, inspectionSegmentation.fileId]);

  // While the user is parked on the "restore still in progress" placeholder
  // page, listen for the server's RestoreDataFinished event and navigate to
  // the actual restored file's URL the moment it lands — so they don't have
  // to manually refresh or hunt for the link in the panel.
  useEffect(() => {
    if (!ocrService.isBeingRestored) return;
    const token = PubSub.subscribe(
      PubSubEvents.RestoreDataFinished,
      (_event: string, notification: any) => {
        const data = notification?.data;
        if (!data) return;
        const guid = data.guid ?? data.Guid;
        const activityType = Number(data.activityType ?? data.ActivityType);
        const success = data.success ?? data.Success;
        if (!success || !guid) return;
        const route = routeForActivity(activityType, guid);
        if (route) navigate(route);
      }
    );
    return () => { PubSub.unsubscribe(token); };
  }, [ocrService.isBeingRestored, navigate]);

  // When the OCR is loaded, add a body class so global UI (top breadcrumb,
  // etc.) can be hidden via CSS for a more "focused" view of the report.
  useEffect(() => {
    if (ocrService.ocrSuccessful) document.body.classList.add("ocr-focused");
    else document.body.classList.remove("ocr-focused");
    return () => document.body.classList.remove("ocr-focused");
  }, [ocrService.ocrSuccessful]);

  const [zoomNotHundred, setZoomNotHundred] = useState(() => {
    if (!window.outerWidth || !window.innerWidth) return false;
    return Math.abs(window.outerWidth / window.innerWidth - 1) > 0.01;
  });
  useEffect(() => {
    const check = () => {
      if (!window.outerWidth || !window.innerWidth) return;
      setZoomNotHundred(Math.abs(window.outerWidth / window.innerWidth - 1) > 0.01);
    };
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // On leaving the page, tear down any hidden full-video frame-capture elements that may
  // still be buffering in the background (they otherwise linger up to 15s and keep decoding
  // the full-res video exactly as the user navigates away).
  useEffect(() => () => { (videoPlayerService as any).abortHiddenCaptures?.(); }, []);

  // Advance to the results step when the file has any result. This MUST run in an
  // effect, not during render: AppPage (SmartAppBase) forces step=1 during render
  // when there is no fileId, so writing step=3 during render here would ping-pong
  // step 1<->3 forever (infinite re-render / page freeze) in the transient state
  // after "New" (fileId cleared but stale VA success still present). See also the
  // fileId-cleared branches in the load effects above, which reset that stale state.
  // A staged redo pulls the page back to step 2 (label & run); AppPage only ever forces a
  // step BELOW 2, so writing 2 here is stable and cannot ping-pong with it.
  useEffect(() => {
    if (redoEditing) ocrApp.setStep(2);
    else if (hasAnyResult) ocrApp.setStep(3);
  }, [hasAnyResult, redoEditing]);

  // OCR results are the usual thing to export, but they are not the only one: an approved
  // analysis-only review has ISYBAU/DWA documents of its own, so requiring OCR here would
  // grey the button out on exactly the file the approval was for. Per-item props below decide
  // what the menu actually offers once it opens.
  const exportDisabled =
    (!ocrService.ocrSuccessful && !inspectionReview.videoAnalysisExportAvailable) ||
    ocrService.isLoading ||
    ocrService.pdfLoading;

  // One shared video for all result tabs. Same underlying file, so we prefer the
  // OCR service's video and fall back to the analysis service (e.g. analysis-only
  // runs where OCR never loaded a video). Seeks from any tab go through the
  // singleton videoPlayerService regardless of which service backs the element.
  const videoService =
    ocrService.video !== undefined ? ocrService : inspectionVideoAnalysis;

  // Result-tab visibility is driven purely by which result DATA the file actually has:
  //  - Inspection Insights (tab 0): shown when OCR data exists (with or without analysis).
  //  - Video Analysis (tab 1): shown when analysis data exists / is running (± OCR).
  //  - Comparison (tab 2): shown ONLY when BOTH OCR and analysis data exist (it compares
  //    the two, so it's meaningless with just one).
  const showInspectionTab = ocrService.ocrSuccessful;
  const showAnalysisTab =
    inspectionVideoAnalysis.analysisSuccessful || inspectionVideoAnalysis.analysisRunning;
  const showComparisonTab =
    ocrService.ocrSuccessful && inspectionVideoAnalysis.analysisSuccessful;

  // Clamp the active tab to one that's actually visible (e.g. an analysis-only file has no
  // Inspection tab, so fall back to Video Analysis). Order: Inspection, Analysis, Comparison.
  const availableTabs = [
    showInspectionTab ? 0 : null,
    showAnalysisTab ? 1 : null,
    showComparisonTab ? 2 : null,
  ].filter((t): t is number => t !== null);
  const effectiveTab = availableTabs.includes(resultTab) ? resultTab : (availableTabs[0] ?? 0);

  // The machine output for the Video Analysis tab — what its "Analysis output" view shows, and
  // what Approve all copies into the approved list. Only built while that tab is actually open;
  // buildAnalysisDetections walks every prediction in the file.
  const vaDetectionsVisible = effectiveTab === 1 && inspectionVideoAnalysis.analysisSuccessful;
  const originalDetections = useMemo(
    () => (vaDetectionsVisible ? buildAnalysisDetections(inspectionVideoAnalysis) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      vaDetectionsVisible,
      inspectionVideoAnalysis.allPredictions,
      inspectionVideoAnalysis.hasRoiData,
      inspectionVideoAnalysis.roiDetections,
      inspectionVideoAnalysis.analysisConfidenceOption,
      inspectionVideoAnalysis.analysisOverallConfidence,
    ]
  );
  // ---- Inspection Insights tab source ---------------------------------------------------
  // Both tabs open on the machine OUTPUT. Nothing is approved on a fresh run, so opening on the
  // approved list showed an empty Inspection Graph and Detection List; the output is what there
  // is to review. The approved list stays one click away in the selector and remains the
  // reviewed result once a reviewer has approved detections.
  const [ocrSource, setOcrSource] = useState<DetectionSource>("original");
  // The storage drives the table/pipe/cards; "compare" renders its own view, so anything but
  // "original" means the approved list.
  useEffect(() => {
    inspectionInfoStorage.setUseApproved(ocrSource !== "original");
  }, [ocrSource]);

  // The ocr.json rows as a SNAPSHOT, and the dep every memo below keys off.
  //
  // `inspectionInfoStorage.detections` is an MST array that setDetections() fills with
  // `array.replace()` — an IN-PLACE mutation, so the node's identity never changes once the
  // singleton store exists. A useMemo keyed on the array itself therefore ran exactly once, on
  // this page's first render, when the store was still empty, and never recomputed: those lists
  // stayed empty for the whole lifetime of the page. That is what made "Approve all" a no-op
  // after a page refresh — it unions the machine output into the approved list, and its copy of
  // the machine output was permanently empty (nothing to approve => no rows, no Compare). It
  // only appeared to work straight after a run because the run-finished event reloads the
  // boot-gated stores, which unmounts and remounts this page, recomputing every memo against a
  // store that by then had data.
  //
  // `detectionRows` is a computed over the store's SNAPSHOT, so its identity changes exactly when
  // the rows change: the memos now track the DATA rather than the container holding it. The
  // snapshot is taken inside the store because the app and the webclient resolve different
  // mobx-state-tree copies, so MST helpers only work on the side that owns the node.
  const ocrDetectionRows = inspectionInfoStorage.detectionRows;

  // The two OCR-side lists in the shape the side-by-side comparison draws.
  const ocrOutputAsDetections = useMemo(
    () => ocrToAnalysisDetections(ocrDetectionRows, ocrTsToSeconds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ocrDetectionRows]
  );
  const ocrApprovedAsDetections = useMemo(
    () => ocrToAnalysisDetections(inspectionInfoStorage.approvedDetections, ocrTsToSeconds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inspectionInfoStorage.approvedDetections]
  );

  /**
   * PDF export for one tab and one detection source.
   *
   * downloadPdf photographs the live DOM rather than asking the server for a document, so
   * which rows end up in the file is decided by what the page is showing at the moment it
   * runs. An "Approved" PDF therefore has to open the right tab with the approved source
   * selected first, wait for that to paint, and put the view back afterwards — otherwise the
   * menu label and the file's contents disagree.
   */
  const exportPdfFor = useCallback(
    async (tab: number, source: DetectionSource) => {
      if (zoomNotHundred) {
        window.alert("Please press Ctrl+0 to reset browser zoom to 100% before exporting to PDF.");
        return;
      }

      const previousTab = resultTab;
      const previousOcrSource = ocrSource;
      const previousDetectionSource = detectionSource;

      setResultTab(tab);
      if (tab === 0) setOcrSource(source);
      if (tab === 1) setDetectionSource(source);
      ocrApp.toggleCollapsed(true);

      // One wait covers the collapse animation and the re-render from the switches above. The
      // capture is synchronous once it starts, so this has to be settled before it runs.
      await new Promise<void>((resolve) => setTimeout(resolve, 1000));
      await ocrService.downloadPdf();

      setResultTab(previousTab);
      setOcrSource(previousOcrSource);
      setDetectionSource(previousDetectionSource);
    },
    [zoomNotHundred, ocrService, resultTab, ocrSource, detectionSource]
  );

  // The two export menus. A group is declared only when something behind it exists, and
  // ExportMenuButton drops any row whose handler is missing, so neither menu can offer a
  // download that would come back empty.
  //
  // The OCR menu as a whole hangs on the file HAVING an OCR side — showInspectionTab, the same
  // flag that decides the Inspection Insights tab — exactly as the VA menu below hangs on
  // showAnalysisTab. An analysis-only run has no OCR output and nothing to approve on the OCR
  // side, so every row in this menu would have failed on click. The approved group being
  // unconditional is about the REVIEW's state (Approved / Stale / InReview must not change the
  // menu's shape); it was never meant to survive a run where OCR did not happen.
  const ocrExportGroups: ExportGroup[] = showInspectionTab
    ? [
        {
          labelKey: "comparison.export.group.ocr",
          onExportPdf: () => void exportPdfFor(0, "original"),
          onExportCsv: ocrService.downloadCsv,
          onExportIsybau: ocrService.downloadIsybau,
          onExportDwa: ocrService.downloadDwa,
        },
        // Always present, never conditional on the review's state.
        //
        // It used to appear only while the review was Approved, so it vanished the moment a redo made
        // it Stale or a save left it InReview — and a group that disappears is indistinguishable from
        // a feature that does not exist. The heading is the honest place to say "this is the approved
        // record"; whether a particular format is downloadable is the row's business, not the group's.
        {
          labelKey: "comparison.export.group.approved",
          // Everything here describes the OCR side's approved list, and nothing else: the two approved
          // lists produce separate files, so this group and the VA button never hand out the same
          // download. The CSV is built from the review rather than the pipeline blob, which is why it
          // can carry condition and risk at all.
          //
          // Every format, always listed — the menu shows what the approved record CAN produce, not
          // what happens to be on disk this second. Hiding a row until its file existed meant the menu
          // changed shape between saves, and a row that is absent reads as "this app cannot do that"
          // rather than "not generated yet".
          //
          // The cost is that a format with no document behind it fails on click instead of being
          // absent, which is what the error message is for: it says the export could not be downloaded
          // and that approving again regenerates it. In practice both XMLs are written on every
          // approval now — a copy is never withheld — so this only bites before the first approval or
          // after a redo has cleared them.
          onExportPdf: () => void exportPdfFor(0, "approved"),
          onExportCsv: ocrService.downloadApprovedOcrCsv,
          onExportIsybau: ocrService.downloadApprovedOcrIsybau,
          onExportDwa: ocrService.downloadApprovedOcrDwa,
        },
      ]
    : [];

  // Two groups, and the split is the point: the analyzer's own report is the machine output and
  // is handed over untouched, while everything under Approved describes what the reviewer signed
  // off. No STANDARDS document is offered for the raw output — letting a machine guess leave in
  // an official format is the thing approval exists to prevent — but the analyzer's HTML is its
  // own report, plainly labelled as such, so there is nothing to misrepresent.
  // Both are present whenever the file HAS an analysis side, regardless of the review's state —
  // same reasoning as the OCR Approved group: the menu describes what this side can produce, and
  // a shape that changes between saves is what made the entries look like they came and went.
  const vaExportGroups: ExportGroup[] = showAnalysisTab
    ? [
        {
          labelKey: "comparison.detection-source.analysisOutput",
          onExportHtml: inspectionVideoAnalysis.downloadAnalysisHtml,
        },
        {
          labelKey: "comparison.export.group.approved",
          onExportPdf: () => void exportPdfFor(1, "approved"),
          // Same columns as the OCR CSV: one format for either side.
          onExportCsv: inspectionVideoAnalysis.downloadApprovedAnalysisCsv,
          // The reviewed counterpart of the analyzer's report — generated from the review, not
          // filtered out of the pipeline's file.
          onExportHtml: inspectionVideoAnalysis.downloadApprovedAnalysisHtml,
          onExportIsybau: ocrService.downloadVideoAnalysisIsybau,
          onExportDwa: ocrService.downloadVideoAnalysisDwa,
        },
      ]
    : [];

  // Each side's MACHINE OUTPUT in review-DTO shape. This is what an empty approved list gets
  // seeded from on the first edit, so it must match exactly what the corresponding list shows.
  const ocrMachineRows = useMemo(
    () =>
      (ocrDetectionRows ?? []).map((d: any) => ({
        side: "Ocr" as const,
        source: "OCR",
        decision: "Accepted",
        mainCode: d.main_Code ?? "",
        distance: Number(d.distance ?? 0),
        timeSeconds: ocrTsToSeconds(d.timestamp),
        note: d.text ?? "",
        c1: d.c1 ?? "",
        c2: d.c2 ?? "",
        q1: d.q1 ?? "",
        q2: d.q2 ?? "",
        sa: d.sa ?? "",
        positionFrom: d.postionFrom ?? "",
        positionTo: d.positionTo ?? "",
        // The frame's durable identity plus its position in the video. The URL is display-only
        // and goes in its own field: it is a SAS link, so it cannot be stored, and putting it in
        // capturedFrame (which means "bytes to upload") is what left every extracted damage
        // exporting with an empty Fotodatei. The server re-finds the image from frameIndex.
        frameIndex: d.frameIndex ?? undefined,
        machineFrameUrl: d.frameUrl || undefined,
        confidence: d.confidence ?? 0,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ocrDetectionRows]
  );

  const analysisMachineRows = useMemo(
    () =>
      inspectionVideoAnalysis.analysisSuccessful
        ? analysisToReviewedDetections(buildAnalysisDetections(inspectionVideoAnalysis))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      inspectionVideoAnalysis.analysisSuccessful,
      inspectionVideoAnalysis.allPredictions,
      inspectionVideoAnalysis.hasRoiData,
      inspectionVideoAnalysis.roiDetections,
      inspectionVideoAnalysis.analysisConfidenceOption,
      // The remaining inputs buildAnalysisDetections() actually reads. vaData is frozen and
      // replaced wholesale on every (re)load, the overall confidence is what request() resets
      // to the file's recommended level, and the OCR rows drive its ground-truth matching — so
      // without these the seed list could keep describing the state the page mounted in.
      inspectionVideoAnalysis.vaData,
      inspectionVideoAnalysis.analysisOverallConfidence,
      ocrDetectionRows,
    ]
  );

  // The analysis run's time -> metre track (the Distances table in -analysis.json). OCR output
  // carries no such track, so a row added on the Inspection Insights tab borrows this one to get
  // its station from the video position. Empty -- and therefore no pre-fill -- without analysis.
  const analysisDistanceTrack = useMemo(
    () => distanceTrack(inspectionVideoAnalysis),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inspectionVideoAnalysis.vaData]
  );
  const distanceForTime = useCallback(
    (seconds: number) => interpolatedDistanceAtTime(analysisDistanceTrack, seconds),
    [analysisDistanceTrack]
  );

  // Main information is one per review, not per side, so every save carries the same set.
  const mainInformation = useMemo(() => {
    const fromOcr = (ocrService.mainInformation ?? []).map((m: any) => ({
      key: m.key,
      value: m.value,
    }));
    if (fromOcr.length > 0) return fromOcr;

    // A run without OCR has no ocr.json to populate the service, so after a reload this is empty
    // while the review still holds the header the reviewer typed. The review is then the only
    // copy there is, and a save built from the empty one would hand the server nothing to keep.
    return (inspectionReview.mainInformation ?? []).map((m: any) => ({
      key: m.key,
      value: m.value,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocrService.mainInformation, reviewMainInfoSnapshot]);

  // The two approved lists. Everything that changes a detection on either tab goes through
  // these: same seeding rule, same per-detection verdict, same side-scoped endpoint. The
  // machine output (ocr.json / the analysis output) is never written to.
  const ocrApprovedList = useApprovedList({
    side: "Ocr",
    review: inspectionReview,
    machineRows: ocrMachineRows,
    mainInformation,
  });

  // The analysis side starts a header rather than waiting for one. A VA run reads nothing off
  // the video, so without this its first save carries no main information, the sign-off is
  // refused, and the reviewer gets a stored list and no documents with nothing to explain it.
  // Field by field: a reviewer who had entered a Haltung and nothing else used to count as
  // having a header, and the document went out with an empty Inspektionsrichtung -- which the
  // ISYBAU schema rejects, so that side produced an invalid file while the DWA passed.
  const analysisMainInformation = useMemo(
    () => withAnalysisHeaderDefaults(mainInformation),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mainInformation]
  );

  const analysisApprovedList = useApprovedList({
    side: "Analysis",
    review: inspectionReview,
    machineRows: analysisMachineRows,
    mainInformation: analysisMainInformation,
  });

  // Everything below is built from the two lists ABOVE rather than from the loaded review, so
  // the graph, the cards and the comparison all reflect the reviewer's unsaved edits too.
  const hasOcrApprovedList = ocrApprovedList.hasApproved;
  const hasApprovedList = analysisApprovedList.hasApproved;

  // The OCR output indexed by the row identity the review stores (distance + main code), so an
  // approved row can borrow any detail it does not carry itself.
  const ocrDetectionByKey = useMemo(() => {
    const byKey = new Map<string, any>();
    ocrDetectionRows.forEach((d) =>
      byKey.set(stableKeyFor(d.distance, d.main_Code), d)
    );
    return byKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocrDetectionRows]);

  // Mirror the OCR-side approved rows into the inspection storage, which drives the Inspection
  // Graph, the pipe diagram and the detection cards. They are reshaped into the OCR detection
  // shape those consumers read, and carry their own status / condition / risk. Empty until
  // something is approved — the storage no longer falls back to ocr.json.
  //
  // Anything the stored row is MISSING is filled from the matching OCR detection. Lists written
  // by earlier versions kept only the fields the review needed (station, codes, verdict), so
  // read back on their own they render "Invalid Date" for the time and a blank Detections
  // column. The reviewer's own values always win — the fallback is nullish, so a field they
  // deliberately cleared stays cleared.
  useEffect(() => {
    inspectionInfoStorage.setApprovedDetections(
      ocrApprovedList.rows.map((d: any) => {
        const ocr = ocrDetectionByKey.get(stableKeyFor(d.distance, d.mainCode));
        return {
          // The row's ORIGINAL identity, carried through so the per-field "edited by reviewer"
          // marks can still find the machine row after the station or main code -- which is what
          // identity is made of -- was itself the correction. The server stamps this when the row
          // is created and never rewrites it.
          stableKey: d.stableKey,
          // The video position as a NUMBER, next to the clock string below. It is the third part
          // of the row's identity, and Edit and Delete match on it -- rebuilding it from the
          // rendered string is a round-trip that only happens to be lossless.
          timeSeconds: d.timeSeconds,
          timestamp:
            d.timeSeconds !== undefined && d.timeSeconds !== null
              ? secondsToOcrTimestamp(d.timeSeconds)
              : ocr?.timestamp ?? "",
          text: d.note ?? ocr?.text ?? "",
          distance: Number(d.distance ?? 0),
          postionFrom: d.positionFrom ?? ocr?.postionFrom ?? "",
          positionTo: d.positionTo ?? ocr?.positionTo ?? "",
          // A reviewer's capture, else this row's own extracted frame, else the machine row's by
          // station. The last fallback is what keeps an image on screen for a row seeded before
          // frameIndex was carried through, and it is the one that breaks when the station is
          // edited — hence the middle term, which does not depend on the key matching.
          frameUrl: d.capturedFrame ?? d.frameUrl ?? d.machineFrameUrl ?? ocr?.frameUrl ?? "",
          main_Code: d.mainCode ?? "",
          c1: d.c1 ?? ocr?.c1 ?? "",
          c2: d.c2 ?? ocr?.c2 ?? "",
          q1: d.q1 ?? ocr?.q1 ?? "",
          q2: d.q2 ?? ocr?.q2 ?? "",
          sa: d.sa ?? ocr?.sa ?? "",
          addedByUser: d.decision === "Added" || d.source === "User",
          confidence: d.confidence ?? 0,
          source: d.source ?? "",
          decision: d.decision ?? "",
          // The verdict travels ON the row, the same way the analysis side carries it.
          detectionStatus: d.detectionStatus,
          conditionRating: d.conditionRating,
          riskRating: d.riskRating,
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocrApprovedList.rows, ocrDetectionByKey]);

  // The analysis-side rows in the shape its Inspection Graph draws. No fallback to the model
  // output: an unreviewed analysis draws an empty graph.
  const approvedDetections = useMemo(
    () => reviewedToAnalysisDetections(analysisApprovedList.rows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisApprovedList.rows]
  );

  // ---- Approving one damage from the Analysis output graph --------------------------------
  // Everything held for the analysis side, by the identity the review stores (station + main
  // code). allRows, not rows: a damage the reviewer un-ticked is still here, and re-ticking it
  // has to find the row it already has rather than append a second one.
  const analysisRowByKey = useMemo(() => {
    const byKey = new Map<string, ReviewedDetectionDto>();
    analysisApprovedList.allRows.forEach((d) =>
      byKey.set(stableKeyFor(d.distance, d.mainCode, d.timeSeconds), d)
    );
    return byKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisApprovedList.allRows]);

  // The row whose frame is being grabbed. One at a time, deliberately: a capture seeks a hidden
  // copy of the video and takes about a second, so approving twenty damages at once would lock
  // the page up. This way the reviewer keeps working while each frame arrives.
  const [approvingRowId, setApprovingRowId] = useState<string | null>(null);

  const toggleAnalysisApproval = useCallback(
    async (row: AnalysisDetection, next: boolean) => {
      const previous = analysisRowByKey.get(
        stableKeyFor(row.meters, row.mainCode, row.timestamp)
      );

      if (!next) {
        // Off Approved, not deleted: the row stays stored so the rejection survives a reload
        // and Approve all can bring it back. Nothing is written until Save list either way.
        if (previous) {
          analysisApprovedList.upsert({ ...previous, detectionStatus: "Draft" }, previous);
        }
        return;
      }

      // The frame the damage was seen at, so the row arrives complete — picture and data — and
      // the reviewer edits it rather than hunting the video for the position again. Skipped when
      // the row already has one: a capture is the reviewer's own, and approval redraws its
      // caption anyway, so re-grabbing would only throw away what they chose.
      let capturedFrame: string | undefined = (previous as any)?.capturedFrame;
      if (!capturedFrame && !previous?.frameFileName && ocrService.fileId !== undefined) {
        setApprovingRowId(row.id);
        try {
          capturedFrame =
            (await (videoPlayerService as any).captureScreenshotAt(
              ocrService.fileId,
              row.timestamp
            )) || undefined;
        } catch (error) {
          // A frame that would not capture must not stop the damage being approved: the row is
          // still a finding, and the camera button on the card is there to try again.
          console.error("Frame capture failed:", error);
        } finally {
          setApprovingRowId(null);
        }
      }

      analysisApprovedList.upsert(
        {
          ...(previous ?? {}),
          side: "Analysis",
          // An untouched model proposal, even though the reviewer is the one accepting it —
          // provenance is about who FOUND the damage, and Compare reads it that way.
          source: previous?.source ?? "AI",
          decision: previous?.decision ?? "Accepted",
          confidence: row.accuracy,
          mainCode: row.mainCode,
          distance: row.meters,
          timeSeconds: row.timestamp,
          c1: row.c1 ?? "",
          c2: row.c2 ?? "",
          // A description written from the analysis data, so the observation text in the
          // documents is not blank. Never overwrites one that is already there.
          note: previous?.note || analysisObservationText(row),
          capturedFrame,
          detectionStatus: "Approved",
        } as ReviewedDetectionDto,
        previous
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisRowByKey, analysisApprovedList, ocrService.fileId]
  );

  /**
   * Accept the Comparison tab's checked AI proposals onto the ANALYSIS approved list.
   *
   * Checking a row there folds it into the ground truth, which is the OCR side's record. The
   * analysis list is approved separately and produces its own documents, so it has to be told as
   * well.
   *
   * Three cases, and getting one of them wrong is what made this add nothing at all:
   *
   * 1. **Not on the list** -- inserted, with its frame.
   * 2. **On the list but not approved** -- APPROVED IN PLACE, keeping every field the reviewer
   *    had already edited. This is the normal case, and it used to be treated as case 3. The
   *    list's first write copies the WHOLE machine output (useApprovedList invariant 2), so every
   *    proposal this tab can offer is already stored as a Draft row the moment the reviewer saves
   *    once. Testing mere PRESENCE therefore skipped all ten of ten, while the list on screen --
   *    approved rows only -- still showed just the two that had been ticked by hand.
   * 3. **Already approved** -- left alone. Re-upserting would overwrite a correction.
   */
  const addCheckedToAnalysisApproved = useCallback(
    async (
      rows: Array<{
        mainCode: string;
        c1: string;
        c2: string;
        meters: number;
        timestamp: number;
        accuracy?: number;
      }>,
      onProgress?: (done: number, total: number) => void
    ) => {
      try {
      // Seeded from what is stored and extended as we go: the map above is captured at render,
      // so without this the same damage appearing twice in one batch would be handled twice. Rows
      // rather than keys, because deciding what to do with one needs its current verdict.
      const seen = new Map(analysisRowByKey);
      // Left in deliberately. This runs a request per row and then one write; when it does
      // nothing the reviewer needs to be able to see WHY without a debugger, and "nothing
      // happened" has already cost us two rounds of guessing.
      // Counted with forEach rather than [...seen.values()].filter(): this project targets ES5
      // without downlevelIteration, so spreading an iterator does not compile.
      let approvedAlready = 0;
      seen.forEach((stored) => {
        if (isApprovedRow(stored)) approvedAlready++;
      });
      console.info("[VA approved] batch start", {
        incoming: rows.length,
        onListAlready: seen.size,
        approvedAlready,
        fileId: ocrService.fileId,
      });
      let added = 0;
      let promoted = 0;
      let skipped = 0;
      let done = 0;

      for (const row of rows) {
        const key = stableKeyFor(row.meters, row.mainCode, row.timestamp);
        const existing = seen.get(key);
        const step = () => onProgress?.(++done, rows.length);

        // Already a result. Nothing to do, and nothing may be overwritten.
        if (existing && isApprovedRow(existing)) {
          skipped++;
          step();
          continue;
        }

        // Stored but not approved: this is the row the reviewer is accepting. Approve it where it
        // is -- every field they have edited stays theirs, and only what is genuinely empty gets
        // filled in. Replacing it with a fresh row built from the comparison marker would throw
        // those edits away.
        if (existing) {
          let frame: string | undefined = (existing as any).capturedFrame;
          if (!frame && !existing.frameFileName && ocrService.fileId !== undefined) {
            try {
              frame =
                (await (videoPlayerService as any).fetchScreenshotAt(
                  ocrService.fileId,
                  existing.timeSeconds ?? row.timestamp
                )) || undefined;
            } catch (error) {
              console.error("Frame capture failed:", error);
            }
          }

          const approvedRow = {
            ...existing,
            decision: existing.decision ?? "Accepted",
            detectionStatus: "Approved",
            capturedFrame: frame,
            note: (existing.note ?? "").trim()
              ? existing.note
              : analysisObservationText({
                  id: key,
                  mainCode: existing.mainCode ?? row.mainCode,
                  c1: existing.c1 ?? row.c1,
                  c2: existing.c2 ?? row.c2,
                  accuracy: existing.confidence ?? row.accuracy ?? 0,
                  meters: Number(existing.distance ?? row.meters),
                  timestamp: Number(existing.timeSeconds ?? row.timestamp),
                }),
          } as ReviewedDetectionDto;

          analysisApprovedList.upsert(approvedRow, existing);
          seen.set(key, approvedRow);
          promoted++;
          step();
          continue;
        }

        // The SERVER-side grab, not the hidden-video capture the single checkbox uses. That one
        // creates a fresh <video> per frame and reloads the whole file to seek it -- fine once, but
        // ten times over a two-gigabyte video is minutes of buffering with the page apparently
        // hung. This endpoint exists for exactly this case: one request per row, no video element.
        let capturedFrame: string | undefined;
        if (ocrService.fileId !== undefined) {
          try {
            capturedFrame =
              (await (videoPlayerService as any).fetchScreenshotAt(
                ocrService.fileId,
                row.timestamp
              )) || undefined;
          } catch (error) {
            console.error("Frame capture failed:", error);
          }
        }

        analysisApprovedList.upsert({
          side: "Analysis",
          source: "AI",
          decision: "Accepted",
          confidence: row.accuracy,
          mainCode: row.mainCode,
          distance: row.meters,
          timeSeconds: row.timestamp,
          c1: row.c1,
          c2: row.c2,
          note: analysisObservationText({
            id: key,
            mainCode: row.mainCode,
            c1: row.c1,
            c2: row.c2,
            accuracy: row.accuracy ?? 0,
            meters: row.meters,
            timestamp: row.timestamp,
          }),
          capturedFrame,
          detectionStatus: "Approved",
        } as ReviewedDetectionDto);
        // Recorded so a duplicate later in the same batch reads as approved, not as absent.
        seen.set(key, {
          mainCode: row.mainCode,
          detectionStatus: "Approved",
        } as ReviewedDetectionDto);
        added++;
        step();
      }

      // Saved UNCONDITIONALLY, not only when this click added something.
      //
      // A press that adds nothing is the normal case on the second press: the rows are already
      // on the list. They may still be sitting there UNSAVED from an earlier press, and gating
      // the write on `added > 0` is what made this a silent no-op -- nothing written, no tab
      // switch, no message. save() is a no-op when there is genuinely nothing pending, and it
      // still signs the list off, which is what writes the documents.
      await analysisApprovedList.save();

      // Land on the rows either way, so the action always shows its result.
      setResultTab(1);
      setDetectionSource("approved");

      console.info("[VA approved] batch done", {
        added,
        promoted,
        skipped,
        listNow: analysisApprovedList.allRows.length,
      });

        // The counts, on screen rather than in the console. "Nothing happened" is
        // indistinguishable from "everything was already there" and from "the rows were added
        // but the reload lost them", and a reviewer cannot be asked to open a debugger to tell
        // them apart. translate() returns an unknown key verbatim, which is how a composed
        // sentence reaches the snackbar (the same route the server's own messages take).
        // Promoted counts as approved: from the reviewer's side "added to the approved list"
        // is one outcome, whether the row had to be created or only signed off.
        const approved = added + promoted;
        const result =
          `${translationService.translate("comparison.addChecked.result")}` +
          ` +${approved} (${added} new, ${promoted} approved) / already ${skipped}`;
        // showError for a press that approved nothing: there is no warning level, and a green
        // tick on a press that changed nothing is the misreport that started this.
        if (approved > 0) snackbarNotificationsService.showSuccess(result);
        else snackbarNotificationsService.showError(result);
      } catch (error) {
        // Anything thrown in here used to escape as an unhandled rejection: the overlay closed
        // and the page looked exactly like a press that had done nothing.
        console.error("[VA approved] batch failed", error);
        snackbarNotificationsService.showError("general.error");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisRowByKey, analysisApprovedList, ocrService.fileId]
  );

  const analysisGraphApproval = useMemo(
    () => ({
      isApproved: (row: AnalysisDetection) =>
        analysisRowByKey.get(stableKeyFor(row.meters, row.mainCode, row.timestamp))
          ?.detectionStatus === "Approved",
      onToggle: (row: AnalysisDetection, next: boolean) =>
        void toggleAnalysisApproval(row, next),
      pendingId: approvingRowId,
      disabled: analysisApprovedList.saving,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisRowByKey, toggleAnalysisApproval, approvingRowId, analysisApprovedList.saving]
  );

  // ---- Comparison tab: which pair of lists to put head to head --------------------------
  // "raw" = OCR output vs analysis output (how the model did against the video overlay).
  // "approved" = OCR approved list as ground truth vs analysis approved list (how the two
  // signed-off lists differ), which only means anything once BOTH sides have been reviewed.
  const canCompareApproved = hasOcrApprovedList && hasApprovedList;
  useEffect(() => {
    if (!canCompareApproved) setComparisonMode("raw");
  }, [canCompareApproved]);

  // The OCR-side approved list in the ground-truth shape the Comparison tab reads.
  const approvedGroundTruth = useMemo(
    () =>
      ocrApprovedList.rows.map((d: any) => ({
        main_Code: d.mainCode ?? "",
        distance: Number(d.distance ?? 0),
        timestamp:
          d.timeSeconds !== undefined && d.timeSeconds !== null
            ? secondsToOcrTimestamp(d.timeSeconds)
            : "",
        text: d.note ?? "",
        c1: d.c1 ?? "",
        c2: d.c2 ?? "",
        q1: d.q1 ?? "",
        q2: d.q2 ?? "",
        sa: d.sa ?? "",
        postionFrom: d.positionFrom ?? "",
        positionTo: d.positionTo ?? "",
        frameUrl: d.capturedFrame ?? d.frameUrl ?? d.machineFrameUrl ?? "",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ocrApprovedList.rows]
  );

  // The analysis-side approved list in the marker shape, standing in for the AI proposals.
  const approvedMarkers = useMemo(
    () =>
      analysisApprovedList.rows.map((d: any) => ({
        mainCode: d.mainCode ?? "",
        c1: d.c1 ?? "",
        c2: d.c2 ?? "",
        accuracy: Number(d.confidence ?? 0),
        meters: Number(d.distance ?? 0),
        timestamp: Number(d.timeSeconds ?? 0),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisApprovedList.rows]
  );

  // The Damage Detection workbench rows, for the Comparison tab's "In Workbench" badges.
  const workbenchRows = useMemo(
    () =>
      analysisApprovedList.rows.map((d) => ({
        mainCode: d.mainCode ?? "",
        meters: Number(d.distance ?? 0),
        timestamp: d.timeSeconds ?? undefined,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisApprovedList.rows]
  );

  // Main Information edited from its table. An analysis-only run has no ocr.json to hold it,
  // so this is one of the two actions (the other being a detection edit) that first creates
  // the review record — persist it right away instead of leaving it in local state until the
  // reviewer happens to also press Save in the sign-off header, which silently lost the edit
  // on a reload.
  const saveMainInformation = useCallback(
    async (items: { key: string; value: string }[]) => {
      ocrService.setMainInformation(items);
      if (ocrService.fileId === undefined) return;

      // Which lists were signed off BEFORE this edit. Only those are re-approved afterwards, so
      // their documents pick up the new header. Read now: the save below demotes the OCR side to
      // InReview, after which it would look as though it had never been approved.
      const wasApproved = {
        Ocr: inspectionReview.isApprovedFor("Ocr"),
        Analysis: inspectionReview.isApprovedFor("Analysis"),
      };
      // Main information is part of every generated document — it is the header the ISYBAU and
      // DWA files are built around — so a change to it has to reach them. Saving alone cannot:
      // the write goes out as InReview, and the documents are produced by approval. So the review
      // is signed off straight afterwards whenever the server now allows it.
      //
      // Whenever it ALLOWS it, not only when it was already approved: on a run without OCR the
      // missing header is the very thing that refused the approval, so the moment it is entered
      // is the moment the sign-off becomes possible. Waiting for another press of Save list was
      // a dead end — that button greys out once the list is stored.

      // Send the OCR side's OWN detections, not the whole review: a side-scoped save re-tags
      // every row it carries, so including the analysis rows here would move them to the OCR
      // list. Main information itself is shared and goes out unchanged either way.
      const saved = await inspectionReview.save({
        status: "InReview",
        comment: inspectionReview.comment,
        side: "Ocr",
        detections: inspectionReview.ocrDetections,
        // Never sent empty. The server REPLACES main information with whatever a save carries,
        // so a dialog closed with every box blank would delete a header that was already there.
        mainInformation: items.length > 0
          ? items.map((m) => ({ key: m.key, value: m.value }))
          : undefined,
      });
      // save() has already said why in a snackbar.
      if (saved === false) return;

      // Re-sign, side by side, only what was signed off before. This used to call approve() with
      // no side, which approves the WHOLE review: a header edit signed off a list the reviewer had
      // never approved, and tried to build that side's documents too -- whose refusal or failure
      // then surfaced as an error right after a save that had in fact worked. A list that was not
      // approved stays that way; its own Save list is how it gets signed off.
      for (const side of ["Ocr", "Analysis"] as const) {
        if (!wasApproved[side]) continue;
        const allowed =
          side === "Analysis" ? inspectionReview.analysisCanApprove : inspectionReview.canApprove;
        if (allowed) await inspectionReview.approve(side);
      }
    },
    [ocrService]
  );

  // If the user opened the Insights URL while the backup is still being
  // restored on the server, the file row is just a zip placeholder — the
  // normal label/run UI would be empty and confusing. Show a clear
  // "restoration in progress" message instead until the restore completes.
  if (ocrService.isBeingRestored) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          p: 4,
        }}
      >
        <Paper elevation={4} sx={{ p: 5, maxWidth: 520, textAlign: "center" }}>
          <Restore sx={{ fontSize: 72, color: "primary.main", mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 1.5 }}>
            {translationService.translate("restoreEsicData.openPage.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {translationService.translate("restoreEsicData.openPage.message")}
          </Typography>
          <CircularProgress />
        </Paper>
      </Box>
    );
  }

  // ---- Per-process progress tracker (Step 2) ---------------------------------
  // Honest, state-based feedback while the selected processes run in parallel.
  // No backend % exists, so each running step shows an indeterminate bar + a live
  // elapsed timer (handled inside ProcessProgressTracker). Steps are derived from
  // the same getters the page already reacts to.
  const trackerVideo = ocrService.video;
  const conversionInFlight = !!trackerVideo?.isConverting;
  const conversionDidFail = !!trackerVideo?.conversionFailed;
  const anyProcRunning =
    ocrService.ocrRunning ||
    inspectionVideoAnalysis.analysisRunning ||
    inspectionSegmentation.segmentationRunning;
  const anyProcPending =
    (runAnalysis && inspectionVideoAnalysis.analysisNotStarted) ||
    (runSegmentation && inspectionSegmentation.segmentationNotStarted);

  // Labeling is frozen while the file is busy. During conversion there is no player on screen
  // to grab a frame from (the dialog opens on an empty screenshot); during a run the job has
  // already been submitted with the regions as they were, so re-drawing them changes nothing
  // about what is executing and leaves the stored ROIs out of step with the result that comes
  // back. Nothing running means it lifts by itself — which is the state both New (no file at
  // all) and a staged Redo (nothing submitted yet) land in, so the buttons come back editable
  // there without needing to be re-enabled explicitly.
  const labelingLocked = conversionInFlight || anyProcRunning;

  const trackerSteps: ProcessStep[] = [];
  // Conversion — while converting/failed, or just-finished while something still runs.
  if (
    trackerVideo !== undefined &&
    (trackerVideo.isConverting ||
      trackerVideo.conversionFailed ||
      (trackerVideo.isAvailable && (anyProcRunning || anyProcPending)))
  ) {
    trackerSteps.push({
      key: "conversion",
      label: translationService.translate("ocrService.progress.step.conversion"),
      state: trackerVideo.conversionFailed
        ? "failed"
        : trackerVideo.isConverting
        ? "running"
        : "success",
      detail: trackerVideo.conversionErrorMessage || undefined,
      // Files uploaded before conversion timings were recorded fall back to the upload time —
      // conversion starts right after the upload, so it is the closest honest start available.
      startedAt: trackerVideo.conversionStartedAt ?? trackerVideo.uploadedDate,
    });
  }
  // OCR is user-gated: only appears once it has actually left the not-started state
  // (so it's never shown "stuck pending" while the user is still labeling). Gated on the
  // BACKEND state, not the local `selection` — after a refresh (or when opening a running
  // job from App Usage) `selection` resets to its default, but OCR only ever leaves
  // "not started" once actually run, so this recovers the running/finished indicator
  // without depending on the (lost) client-side selection.
  if (!ocrService.ocrNotStarted) {
    trackerSteps.push({
      key: "ocr",
      label: translationService.translate("ocrService.progress.step.ocr"),
      state: ocrService.ocrSuccessful ? "success" : ocrService.ocrFailed ? "failed" : "running",
      detail: ocrService.errorMessage ?? undefined,
      startedAt: ocrService.startedAt,
    });
  }
  // Analysis / Segmentation auto-run and may wait for conversion. If conversion failed
  // they can never run, so a still-pending one is shown as failed (keeps the tracker
  // from hanging on a permanent "pending"). Show the step whenever the BACKEND says it has
  // been touched (running/success/failed) — refresh-safe — OR, in-session, while it's a
  // selected add-on still waiting on conversion (the only pre-backend "pending" window).
  // "Requested" is not "running". The backend marks analysis/segmentation in progress as soon as
  // the user asks for it, but it holds the actual Job Manager submission back while the video is
  // still converting, or while the OTHER of the two is in flight (they collide on one task ID).
  // startedAt is stamped at SUBMISSION, so a run that is in progress with no start has not begun:
  // show it as pending — hourglass, no progress bar, no elapsed counter — instead of a spinner
  // counting up time it never spent working. It flips to running the moment the backend submits.
  if (!inspectionVideoAnalysis.analysisNotStarted || (runAnalysis && conversionInFlight)) {
    const state: ProcessState = inspectionVideoAnalysis.analysisSuccessful
      ? "success"
      : inspectionVideoAnalysis.analysisFailed
      ? "failed"
      : inspectionVideoAnalysis.analysisRunning
      ? inspectionVideoAnalysis.startedAt
        ? "running"
        : "pending"
      : conversionDidFail
      ? "failed"
      : "pending";
    trackerSteps.push({
      key: "analysis",
      label: translationService.translate("ocrService.progress.step.analysis"),
      state,
      detail: inspectionVideoAnalysis.errorMessage ?? undefined,
      startedAt: inspectionVideoAnalysis.startedAt,
    });
  }
  if (
    !inspectionSegmentation.segmentationNotStarted ||
    // Selected but not sent yet: waiting for conversion, or for the analysis that holds the task
    // ID. Without this the step vanishes for the whole analysis run and a three-way order shows
    // only two of its services.
    (runSegmentation && (conversionInFlight || inspectionVideoAnalysis.analysisRunning))
  ) {
    const state: ProcessState = inspectionSegmentation.segmentationSuccessful
      ? "success"
      : inspectionSegmentation.segmentationFailed
      ? "failed"
      : inspectionSegmentation.segmentationRunning
      ? inspectionSegmentation.startedAt
        ? "running"
        : "pending"
      : conversionDidFail
      ? "failed"
      : "pending";
    trackerSteps.push({
      key: "segmentation",
      label: translationService.translate("ocrService.progress.step.segmentation"),
      state,
      detail: inspectionSegmentation.errorMessage ?? undefined,
      startedAt: inspectionSegmentation.startedAt,
    });
  }
  // A staged redo has started nothing, so there is no progress to report — the tracker stays
  // away until the user actually presses Run.
  // Order & process details and Label Detection Settings, as one tabbed panel. Shown in the
  // header while a run is in progress (what is running, what it costs), and beside Main
  // Information once the results are up.
  const orderPanel = (
    <OrderAndLabelsPanel
      ocrService={ocrService}
      order={
        <OrderProcessSummary
          ocrService={ocrService}
          analysisService={inspectionVideoAnalysis}
          segmentationService={inspectionSegmentation}
          orderStore={orderBreakdownStorage}
          onRerun={(key) => void runGuarded("Re-run", () => rerunService(key))}
          compact
          embedded
        />
      }
    />
  );

  const showTracker =
    trackerSteps.length > 0 && (conversionInFlight || anyProcRunning) && !redoEditing;

  return (
    <SmartAppBase
      appStorage={ocrApp}
      stickyActionBar
      hideCopyUrl
      hideUploadUI={isRestoreInFlight()}
      hideUploadStepHeader={showResults}
      /* Order & process details — what was run, how long it took and exactly what it cost —
         sit in the header, to the right of the app description, where they are visible from
         the moment the run starts through to the results (the header never unmounts). The
         panel renders nothing until at least one service has been ordered. */
      headerDescription={<InspectionInsightsDescription showOutput={ocrService.fileId === undefined} />}
      /* Mounted only once there is a file: with nothing ordered the summary is empty, but its
         slot would still reserve the right-hand column and squeeze the description. */
      /* Until there are results. Once the report is on screen the panel moves beside Main
         Information instead (see the results row), so it is never shown twice. */
      headerAside={ocrService.fileId !== undefined && !showResults ? orderPanel : undefined}
      step1Top={renderServiceChoice(paidSelection !== null)}
      dropInstructionKey="ocrService.upload.drag-and-drop-video"
      purchaseApps={selectedActivities}
      onPurchaseConfirm={startNewUpload}
      hideRedo
      icon={{
        svg: Assignment,
        textKey: "RunOcrServiceButton.run",
        className: themeClass(["icon-custom"]),
      }}
      actionBar={
        <>
          <RedoButton onClick={() => setShowRedo(true)} disabled={ocrApp.step < 2} />
          {ocrExportGroups.length > 0 && (
            <ExportMenuButton
              id="export-ocr"
              textKey="comparison.export.ocr-button"
              disabled={ocrService.isLoading || ocrService.pdfLoading}
              groups={ocrExportGroups}
            />
          )}
          {vaExportGroups.length > 0 && (
            <ExportMenuButton
              id="export-va"
              textKey="comparison.export.va-button"
              disabled={ocrService.isLoading || ocrService.pdfLoading}
              groups={vaExportGroups}
            />
          )}
          <DownloadMetadataButton
            fileId={ocrService.fileId}
            activityType={ocrApp.activityType}
            videoName={ocrService.videoIdentifier}
            disabled={exportDisabled}
          />
          <RestoreButton />
          {/* Custom Redo dialog: pick any of OCR / Analysis / Segmentation to re-run. */}
          <WarningBeforeAction
            titleKey="general.redo.confirm.title"
            isVisible={showRedo}
            toggleModal={() => setShowRedo(false)}
            optionalWarningKey={redoHasBalance ? "" : "shop.balance-exceeded"}
            buttonOk={{
              textKey: "general.redo",
              action: () => {
                // Nothing is submitted here. The page returns to step 2 (video + labeling +
                // Run) so the user can re-draw the regions for this run; the Run button then
                // triggers the selected services.
                setShowRedo(false);
                setRedoEditing(true);
              },
              // Require a primary service: !hasPrimary blocks both an empty selection and
              // Segmentation-only. Labeling is NOT required to get here anymore — it is done
              // in the step the confirm leads to, and the Run button enforces it there.
              disabled: !hasPrimary || !redoHasBalance,
            }}
          >
            <div>{translationService.translate("general.redo.confirm.description")}</div>
            <Stack pr={8}>
              {/* Deliberately NOT locked: Redo is the path for changing which services run on
                  an already-paid file, and the price below re-resolves as the selection
                  changes so the new charge is on screen before confirming. */}
              {renderServiceSelector()}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {translationService.translate("ocrService.mode.redoChargeNotice")}
                {selectedServiceLabels && `: ${selectedServiceLabels}`}
              </Typography>
              <OrderOverview price={redoPrice} />
            </Stack>
          </WarningBeforeAction>
        </>
      }
    >
      {isInitiallyLoading ? (
        // Opened file still fetching its first data set: show a content-area spinner (the
        // action bar + header stay put, since they live in SmartApp above AppPage) rather than
        // flashing the fresh-file label/run controls. Clears the instant the first service
        // resolves, so each result tab still appears as soon as it's ready.
        <Grid item xs={12}><LoadingSpinner /></Grid>
      ) : (
        <>
      <SmartAppStep
        step={2}
        titleKey="ocrService.labelVideoAreas.title"
        descriptionKey="ocrService.labelVideoAreas.description"
        // On the results view this step holds only the progress tracker now that the label
        // buttons moved to the header panel. With no tracker it was an empty block whose margin
        // pushed Main Information down, so it is not rendered at all.
        visible={ocrService?.video !== undefined && (!showResults || showTracker)}
        appStorage={ocrApp}
        hideStepHeader={showResults}
      >
        <Grid container rowSpacing={1}>
          {/* Live per-process progress while the selected services run in parallel.
              Lives in step 2 (not step 3) so it stays visible during conversion, when
              step 3 isn't mounted yet. Auto-hides once every step is done/failed. */}
          {showTracker && (
            <Grid item xs={12}>
              <ProcessProgressTracker steps={trackerSteps} />
            </Grid>
          )}
          {/* (The order & process summary lives in the page header, next to the app
              description — see headerAside above. The header is mounted for every step, so
              it covers conversion, the run itself and the results with a single copy.) */}
          {/* A staged redo lands here with nothing started. Restate what pressing Run will
              cost, since the dialog that named it is already gone. */}
          {redoEditing && (
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                {translationService.translate("ocrService.mode.redoChargeNotice")}
                {selectedServiceLabels && `: ${selectedServiceLabels}`}
              </Typography>
            </Grid>
          )}
          {/* Service selector + label-and-run video: only for a fresh (unprocessed) file. */}
          {/* Post-payment: the charge for this file has already been taken, so the set is
              frozen here too — changing it would run something that was never billed. */}
          {showRunControls && renderServiceSelector(paidSelection !== null)}
          {showRunControls && (
            <Grid item xs={12} sm={12} xl={8}>
              {ocrService.video !== undefined && (
                <Player service={ocrService}>
                  {ocrService?.video!.isAvailable && <VideoPlayer enableControls />}
                  <LabelingInfo type="flow" clickType="button" iconSx= {{position: "absolute", top: 8, right: 8}} />
                </Player>
              )}
            </Grid>
          )}
          {/* Results view: no labeling buttons. On a finished file they only replayed the saved
              regions read-only, which the Label Detection Settings tab in the header panel now
              shows directly. They stay for labeling before a run and during a Redo, below. */}
          {showResults ? null : (
            <Grid container item xs={12} sm={12} lg={12} xl={8} sx={{ mt: 1 }}>
              {/* Labeling required whenever a primary (OCR or Analysis) is selected. On a
                  staged redo the previous OCR result is still loaded, so `editable` is what
                  keeps the dialogs writable — without it they would only replay the old
                  regions and the redo would silently reuse them. */}
              {(runOcr || runAnalysis) && (
                <OcrInput ocrService={ocrService} editable={redoEditing} disabled={labelingLocked} />
              )}
              {/* Shown while OCR hasn't run (new or redo). The single Run button kicks
                  off every SELECTED service — startSelected for a fresh file, runStagedRedo
                  after a Redo (it resets the previous result and re-triggers even a service
                  that already succeeded, which startSelected deliberately skips). Labeling is
                  mandatory for every run, so BOTH buttons require inputProvided — the non-OCR
                  button drives Analysis/Segmentation and the page persists the labels first. */}
              {showRunControls && (
                <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  {runOcr ? (
                    <RunOcrServiceButton
                      onClick={() => runGuarded("Run", redoEditing ? runStagedRedo : startSelected)}
                      disabled={!hasPrimary || !ocrService.inputProvided}
                    />
                  ) : (
                    <RunVideoAnalysisButton
                      onClick={() => runGuarded("Run", redoEditing ? runStagedRedo : startSelected)}
                      disabled={!hasPrimary || !ocrService.inputProvided}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </SmartAppStep>

      <SmartAppStep
        step={3}
        /* Mounted from the moment a run starts (resultsPending → the progress panel below)
           through to the finished report (showResults). It stays collapsed to its numbered
           header for everything before that — a fresh file still waiting on Run, and a staged
           redo whose results belong to the previous run — because the result panels would
           otherwise draw an empty report ("There is no Main Information available", an empty
           detection list) next to the video, which reads as a finished run that found nothing. */
        visible={
          (showResults || resultsPending) &&
          ocrService.video !== undefined &&
          ocrService.video.isConverted
        }
        titleKey="ocrService.inspection-report.title"
        descriptionKey="videoAnalysis.inspection-report.description"
        appStorage={ocrApp}
        hideStepHeader={showResults}
      >
        {resultsPending ? (
          // Running, nothing back yet: report the state where the report will be, and keep the
          // empty panels and the result video out of the DOM until there is something in them.
          <Box
            sx={{ px: 2, width: "100%", display: "flex", justifyContent: "center" }}
            data-html2canvas-ignore
          >
            <Paper
              variant="outlined"
              sx={{ p: 4, width: "100%", maxWidth: 560, textAlign: "center" }}
            >
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {translationService.translate("ocrService.results.pending.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translationService.translate("ocrService.results.pending.description")}
              </Typography>
            </Paper>
          </Box>
        ) : (
        /* One padded wrapper so Main Info, the tabs and the tab content all share
           the same left x (no leaking Grid negative margins / clipping). */
        <Box sx={{ px: 2, width: "100%" }}>
          {/* Main Information — above the tabs (shared by all tabs). Placed in the
              same lg=8 grid column as the body content below so its left AND right
              edges line up exactly with the inspection table. */}
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} lg={8} id="pdf-content-table">
              {/* No file-wide condition / risk / provisional-grade banner here: an inspection
                  routinely mixes a critical fracture with a cosmetic deposit, so the verdict
                  belongs to the individual observation. Status / condition / risk now ride on
                  each Detection List card instead. */}
              <MainInfoTable
                mainInfoStorage={mainInfoStorage}
                onSave={saveMainInformation}
                // Back to exactly what OCR read: the machine values are written as the stored
                // header, which replaces corrections and drops fields the reviewer added.
                onRestore={() =>
                  void saveMainInformation(
                    mainInfoStorage.machineMainInformation.map((m) => ({
                      key: m.key ?? "",
                      value: m.value ?? "",
                    }))
                  )
                }
              />
            </Grid>
            {/* The run's details beside its header: what was ordered and charged, and the
                regions OCR read from. Same lg=4 column as the video below, so the edges align. */}
            <Grid item xs={12} lg={4} data-html2canvas-ignore>
              {orderPanel}
            </Grid>
          </Grid>

          {/* Styled as folder tabs. Plain MUI tabs -- bare labels over a hairline -- read as a row
              of headings, and reviewers did not realise the other results were one click away.
              Each tab now has its own bordered, shaded body and an icon; the open one is lifted
              (white, bold, primary) and sits on a thick bar, joined to the content below. */}
          <Tabs
            value={effectiveTab}
            onChange={(_e, v) => setResultTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            data-html2canvas-ignore
            TabIndicatorProps={{ sx: { height: 4, borderRadius: "4px 4px 0 0" } }}
            sx={{
              mb: 2,
              minHeight: 0,
              borderBottom: 2,
              borderColor: "primary.main",
              "& .MuiTabs-flexContainer": { gap: 0.75 },
              "& .MuiTab-root": {
                minHeight: 0,
                py: 1.25,
                px: 2.5,
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 500,
                color: "text.secondary",
                bgcolor: "action.hover",
                border: 1,
                borderBottom: 0,
                borderColor: "divider",
                borderRadius: "8px 8px 0 0",
                transition: "background-color 120ms, color 120ms",
                "&:hover": { bgcolor: "action.selected", color: "text.primary" },
              },
              "& .MuiTab-root.Mui-selected": {
                color: "primary.main",
                fontWeight: 700,
                bgcolor: "background.paper",
                borderColor: "primary.main",
              },
            }}
          >
            {showInspectionTab && (
              <Tab
                value={0}
                icon={<Article fontSize="small" />}
                iconPosition="start"
                label={translationService.translate("ocrService.tabs.inspection")}
              />
            )}
            {showAnalysisTab && (
              <Tab
                value={1}
                icon={<ManageSearch fontSize="small" />}
                iconPosition="start"
                label={translationService.translate("ocrService.tabs.videoAnalysis")}
              />
            )}
            {showComparisonTab && (
              <Tab
                value={2}
                icon={<CompareArrows fontSize="small" />}
                iconPosition="start"
                label={translationService.translate("ocrService.tabs.comparison")}
              />
            )}
          </Tabs>

          {/* One result is already shown but more fetches are still in flight — tell the user
              additional tabs are on the way (e.g. Inspection loaded, Video Analysis still
              coming) so they don't assume nothing more will appear. */}
          {isLoadingMore && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }} data-html2canvas-ignore>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {translationService.loadingText}
              </Typography>
            </Stack>
          )}

          {/* One overlay for every Save list on the page -- either side, the graph's approve
              column, the Comparison tab -- so a save looks and blocks the same wherever it was
              pressed. inspectionReview.isSaving catches the writes that bypass the two lists. */}
          <SavingBackdrop
            open={
              ocrApprovedList.saving || analysisApprovedList.saving || inspectionReview.isSaving
            }
          />
          {/* Body: tab content on the left, one shared sticky video on the right. The video
              column is a third of the row — wide enough to actually watch, while the report
              keeps the two thirds it needs for the graph and the detection cards. */}
          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              {effectiveTab === 0 && (
                <>
                  {/* Same control as the Video Analysis tab: the untouched ocr.json output, the
                      approved list, or the two side by side. All three feed the Inspection Graph
                      and the Detection List together. */}
                  <DetectionSourceSelector
                    value={ocrSource}
                    onChange={setOcrSource}
                    originalLabelKey="comparison.detection-source.ocrOutput"
                    hasApproved={ocrApprovedList.hasApproved}
                    onApproveAll={ocrApprovedList.approveAll}
                    approving={ocrApprovedList.saving}
                  >
                    {ocrSource === "compare" ? (
                      <DetectionSourceComparison
                        original={ocrOutputAsDetections}
                        approved={ocrApprovedAsDetections}
                      />
                    ) : (
                      <InspectionReport
                        fileId={ocrService.fileId!}
                        onFinished={ocrService.onServiceFinished}
                        ocr={ocrService}
                        segmentationEnabled={canMarkDamage && markDamage}
                        segmentation={inspectionSegmentation}
                        filteredSegmentations={filteredSegmentations}
                        approved={ocrApprovedList}
                        showingMachineOutput={ocrSource === "original"}
                        distanceForTime={distanceForTime}
                      />
                    )}
                  </DetectionSourceSelector>
                </>
              )}

              {effectiveTab === 1 && (
                <>
                  {inspectionVideoAnalysis.analysisNotStarted &&
                    !inspectionVideoAnalysis.isLoading && (
                      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                        <RunVideoAnalysisButton
                          onClick={() => runGuarded("Video Analysis", startVideoAnalysis)}
                        />
                      </Box>
                    )}
                  {inspectionVideoAnalysis.analysisSuccessful && (
                    <>
                      {/* Why this file has no standards documents, stated where the reviewer is
                          working rather than only in a snackbar they may have missed.

                          The documents are produced by the sign-off, and the sign-off can be
                          refused — most often because a run without OCR has no main information
                          to build a header from. That refusal used to be completely silent: the
                          list saved, the frames saved, and the two XMLs simply never appeared,
                          with nothing on screen to say why. The server sends the reason; this
                          shows it until it is fixed. */}
                      {!inspectionReview.isApprovedFor("Analysis") &&
                        !!inspectionReview.analysisApprovalBlockedReason && (
                          <Alert severity="warning" sx={{ mt: 2 }} data-html2canvas-ignore>
                            {inspectionReview.analysisApprovalBlockedReason}
                          </Alert>
                        )}

                      {/* One control for both the graph and the list below it: they describe
                          the same inspection, so they must never show different sources. */}
                      <Box sx={{ mt: 2 }}>
                        <DetectionSourceSelector
                          value={detectionSource}
                          onChange={setDetectionSource}
                          originalLabelKey="comparison.detection-source.analysisOutput"
                          hasApproved={analysisApprovedList.hasApproved}
                          onApproveAll={analysisApprovedList.approveAll}
                          approving={analysisApprovedList.saving}
                        >
                          {detectionSource === "compare" ? (
                            <Box sx={{ mt: 2 }}>
                              <DetectionSourceComparison
                                original={originalDetections}
                                approved={approvedDetections}
                              />
                            </Box>
                          ) : (
                            <>
                              {/* On "approved" the graph draws the approved rows and nothing else —
                                  none approved means a bare pipe with no damages on it, not a
                                  hidden graph. Omitting the prop (the "analysis output" view) lets
                                  the graph build its own rows from the model, which keeps the
                                  confidence slider meaningful there. */}
                              {/* Ticking damages and storing them have to be reachable from the
                                  same screen. The editable list -- which is where Save list normally
                                  lives -- is on the Approved view, so without this the reviewer would
                                  tick a handful of damages here and have nowhere to save them.
                                  Same actions, same list, same rule: nothing is written until Save. */}
                              {detectionSource === "original" && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mt: 2,
                                    flexWrap: "wrap",
                                  }}
                                  data-html2canvas-ignore
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    {translationService.translate(
                                      "comparison.detection-source.approved"
                                    )}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={analysisApprovedList.rows.length}
                                  />
                                  {analysisApprovedList.dirty && (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      label={translationService.translate(
                                        "comparison.approved-list.unsaved"
                                      )}
                                    />
                                  )}

                                  <Box sx={{ flexGrow: 1 }} />

                                  <SaveListButton approved={analysisApprovedList} />
                                  <Button
                                    size="small"
                                    variant="text"
                                    disabled={
                                      !analysisApprovedList.dirty || analysisApprovedList.saving
                                    }
                                    onClick={analysisApprovedList.discard}
                                  >
                                    {translationService.translate("comparison.approved-list.discard")}
                                  </Button>
                                </Box>
                              )}

                              {/* id="pdf-content-table": the anchor OcrService.downloadPdf collects,
                                  the same one the OCR side puts on its InspectionTable
                                  (InspectionReport.tsx). Without it the export gathered only the
                                  main information, the list header and the frame cards, so a PDF of
                                  the VA approved list arrived with no Inspection Graph in it at all.
                                  The graph is autoHeight and footerless, so every row is in the DOM
                                  for the capture rather than only the visible page. */}
                              <Box sx={{ mt: 2 }} id="pdf-content-table">
                                <AnalysisInspectionGraph
                                  videoAnalysis={inspectionVideoAnalysis}
                                  detections={
                                    detectionSource === "approved" ? approvedDetections : undefined
                                  }
                                  // Sign-off belongs on the view that shows what the model found:
                                  // that is the list being triaged. The approved view already has
                                  // its own controls, and every row on it is approved by definition.
                                  approval={
                                    detectionSource === "original" ? analysisGraphApproval : undefined
                                  }
                                />
                              </Box>
                              {/* The reviewer's working list for this run. Lives here rather than
                                  in the Comparison tab because that tab needs OCR as well, leaving
                                  an analysis-only inspection with nowhere to be corrected.

                                  Hidden on the Analysis output view: there it was the same rows as
                                  the graph above it, with every control switched off — a second
                                  read-only copy of what the reviewer is already looking at. The
                                  graph's approve column is how a damage moves across now, and the
                                  editable list is on the Approved view where the edits land. */}
                              {detectionSource !== "original" && (
                                <Box sx={{ mt: 2 }}>
                                  <VaDetectionList
                                    review={inspectionReview}
                                    videoAnalysis={inspectionVideoAnalysis}
                                    videoId={ocrService.fileId}
                                    source={detectionSource}
                                    approved={analysisApprovedList}
                                    segmentationEnabled={canMarkDamage && markDamage}
                                    segmentation={inspectionSegmentation}
                                    filteredSegmentations={filteredSegmentations}
                                  />
                                </Box>
                              )}
                            </>
                          )}
                        </DetectionSourceSelector>
                      </Box>
                    </>
                  )}
                  {/* The model's own instrument, so it belongs to the model's own view.
                      It plots per-timestamp prediction confidence and carries the threshold
                      slider — neither of which an approved list has: those rows are decided
                      observations, not a signal with a confidence curve, so there is nothing
                      here to filter down to them. Worse, the slider silently moves the machine
                      row set that Approve all / Import AI detections would bring in, which on
                      the approved view reads as a control that does nothing. */}
                  {detectionSource === "original" && (
                    <VideoAnalysisDiagramSection videoAnalysis={inspectionVideoAnalysis} />
                  )}
                </>
              )}

              {effectiveTab === 2 && (
                <>
                  {/* What to compare. The raw pairing answers "how did the model do against the
                      video overlay"; the approved pairing answers "how do the two signed-off
                      lists differ", which is only meaningful once both exist. */}
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}
                    data-html2canvas-ignore
                  >
                    <Typography variant="body2" color="text.secondary">
                      {translationService.translate("comparison.mode.label")}
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={comparisonMode}
                      onChange={(_e, next) => next && setComparisonMode(next)}
                    >
                      <ToggleButton value="raw">
                        {translationService.translate("comparison.mode.raw")}
                      </ToggleButton>
                      <Tooltip
                        title={
                          canCompareApproved
                            ? ""
                            : translationService.translate("comparison.mode.approved-hint")
                        }
                      >
                        <span>
                          <ToggleButton
                            value="approved"
                            disabled={!canCompareApproved}
                            sx={{ height: "100%" }}
                          >
                            {translationService.translate("comparison.mode.approved")}
                          </ToggleButton>
                        </span>
                      </Tooltip>
                    </ToggleButtonGroup>
                  </Box>

                  <InspectionComparison
                    ocrService={ocrService}
                    videoAnalysis={inspectionVideoAnalysis}
                    groundTruthOverride={
                      comparisonMode === "approved" ? approvedGroundTruth : undefined
                    }
                    markersOverride={
                      comparisonMode === "approved" ? approvedMarkers : undefined
                    }
                    onAddCheckedToApproved={addCheckedToAnalysisApproved}
                    workbenchRows={workbenchRows}
                    onSaveReview={async (detections, mainInformation) => {
                      // This tab verifies the OCR-side ground truth, so its accept/edit/dismiss
                      // decisions land on the OCR side of the review — the same rows the sign-off
                      // header approves, and therefore the standards export.
                      await inspectionReview.save({
                        status: "InReview",
                        comment: inspectionReview.comment,
                        side: "Ocr",
                        detections,
                        mainInformation,
                      });
                    }}
                  />
                </>
              )}
            </Grid>

            <Grid item xs={12} lg={4} data-html2canvas-ignore>
              <Box
                sx={{
                  position: "sticky",
                  top: "140px",
                  // The panel is sized to the viewport and never scrolls as a whole — the
                  // video must stay fully visible while reading the report next to it.
                  maxHeight: "calc(100vh - 150px)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  // The shared player carries min-width: 640px (VideoPlayer.scss) for the
                  // standalone pages, and ReactPlayer defaults to an inline 640x360 box.
                  // Both overflow this narrow results column and are what put a scrollbar
                  // around the video, so pin the player to the column instead and let the
                  // frame scale to it. !important is required: the sizes are inline styles.
                  '& [class*="video-player-"]': {
                    minWidth: 0,
                    width: "100%",
                    boxSizing: "border-box",
                  },
                  "& #my-video": {
                    width: "100% !important",
                    height: "auto !important",
                  },
                  "& #my-video video": {
                    width: "100% !important",
                    height: "auto !important",
                    // Keeps a tall/portrait frame from eating the whole column; contain
                    // letterboxes rather than distorting when the cap bites.
                    maxHeight: "55vh",
                    objectFit: "contain",
                  },
                }}
              >
                {/* "Mark damage" option — shown ONLY when this file has segmentation data
                    (segmentationSuccessful → canMarkDamage). No segmentation ⇒ no option. */}
                {canMarkDamage && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1, ml: "10px", flexShrink: 0 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={markDamage}
                          onChange={(_e, value) => setMarkDamage(value)}
                        />
                      }
                      label={translationService.translate("ocrService.markDamage.label")}
                    />
                    {markDamage && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={videoPlayerService.showLabels}
                              onChange={(_e, value) => videoPlayerService.setShowLabels(value)}
                            />
                          }
                          label={translationService.translate("ocrService.markDamage.showClassNames")}
                        />
                        <HighlightSettings tooltipTextKey="ocrService.markDamage.settings" />
                      </>
                    )}
                  </Box>
                )}
                {/* The video keeps its full height whatever else is in the column. */}
                {videoService.video !== undefined && (
                  <Box sx={{ flexShrink: 0 }}>
                    <Player service={videoService}>
                      {videoService.video!.isAvailable && (
                        <VideoPlayer enableControls>
                          {/* Inside VideoPlayer so the canvas anchors to the video's own
                              (position:relative) container and the masks land on the frame. */}
                          {canMarkDamage && markDamage && (
                            <SegmentationCanvasOverlay
                              videoSegmentation={inspectionSegmentation}
                              filteredSegmentations={filteredSegmentations}
                            />
                          )}
                        </VideoPlayer>
                      )}
                      {/* No "how to label" button here: by the time this results video renders,
                          labeling has already been done for the run. It stays on the step-2
                          label-and-run video, which is where it is actually needed. */}
                    </Player>
                  </Box>
                )}
                {/* What sits UNDER the video takes the leftover height and scrolls on its own
                    if there isn't enough — so a long details panel or legend never puts a
                    scrollbar around the video itself. */}
                <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
                  {/* Characterization / extracted-insight details stay directly under the
                      video (important), before the segmentation tools. OCR-specific, so tied
                      to the Inspection Insights tab (only visible when OCR data exists). */}
                  {effectiveTab === 0 && (
                    <Box sx={{ mt: 1 }}>
                      <InspectionInsightDetails inspectionInsightStorage={inspectionInfoStorage} />
                    </Box>
                  )}
                  {/* (Order & process details moved to the page header, beside the app
                      description — see headerAside above.) */}
                  {canMarkDamage && markDamage && (
                    <Box sx={{ mt: 1 }} className="legends-container">
                      <VideoTools
                        videoSegmentation={inspectionSegmentation}
                        analysisConfidenceStorage={confidenceStorage}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        )}
      </SmartAppStep>
        </>
      )}
    </SmartAppBase>
  );
});

function routeForActivity(activityType: number, guid: string): string {
  switch (activityType) {
    case 1: return `/video-analysis?guid=${guid}`;
    case 2: return `/video-segmentation?guid=${guid}`;
    case 3: return `/video-conversion?guid=${guid}`;
    case 4: return `/inspection-insights?guid=${guid}`;
    case 5: return `/analysis-and-segmentation?guid=${guid}`;
    case 6: return `/analysis-and-inspection-insights?guid=${guid}`;
    case 7: return `/inspection-insights?guid=${guid}`;
    default: return "";
  }
}
